import {FlusiaIcon,flusiaGlyph} from './ui/FlusiaIcon';
import {useFluPilot} from '../lib/onboarding/consultant/useFluPilot';
import { useDrawerEscape } from '../lib/hooks/useDrawerEscape';
import { useRouteDrawer } from '../lib/hooks/useRouteDrawer';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import hotToast from 'react-hot-toast';
import { useApp } from './AppProvider';
import { FileViewerModal } from './ui/FileViewerModal';
import { ResponsiveModal } from './ui/ResponsiveModal';
import { DriveFile } from '../lib/services/googleDriveService';
import { UnifiedDetailsPage } from '../pages/leads/LeadDetailsPage';
import { DealDetailsPage } from '../pages/deals/DealDetailsPage';
import {
  LayoutDashboard,
  Users,
  FileText,
  Briefcase,
  BarChart,
  Calendar,
  Settings as SettingsIcon,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  DollarSign,
  FolderOpen,
  LifeBuoy,
  File,
  Package,
  ListTodo,
  LogOut,
  MessageSquare,
  Loader2,
  Building2,
  LayoutGrid,
  ClipboardList,
  CalendarClock,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  X,
  CreditCard,
  Receipt,
  Handshake,
  UserCheck,
  Send,
  ScrollText,
  ShieldCheck,
  Sparkles,
  HelpCircle,
  UserCircle,
  Sun,
  Moon,
  Lock,
  BarChart3 as BarChart3Icon,
  Gift,
  ListChecks,
  ArrowLeft
} from 'lucide-react';
import { SearchBar } from './SearchBar';
import { SearchOverlay } from './SearchOverlay';
import { useSearchStore } from '../lib/stores/searchStore';
import { MobileBottomMenu } from './ui/MobileBottomMenu';
import { MenuSkeleton } from './ui/MenuSkeleton';
import { ProfileEditModal } from './profile/ProfileEditModal';
import { TrialExpiredModal } from './TrialExpiredModal';
import { OrganizationArchivedModal } from './OrganizationArchivedModal';
import { PastDueBlockedModal } from './PastDueBlockedModal';
import { PaymentStatusBanner } from './subscription/PaymentStatusBanner';
import { ChangelogModal } from './ui/ChangelogModal';
import { PermissionDeniedPage } from './PermissionDeniedPage';
import { GuideModal } from './ui/GuideModal';
import { FeedbackModal, type FeedbackType } from './ui/FeedbackModal';
import { captureScreenshot } from '../lib/utils/captureScreenshot';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { TeamChatWidget } from './team-chat/TeamChatWidget';
import { WhatsAppDrawer } from './whatsapp-overlay/WhatsAppDrawer';
import { useWhatsAppUnreadTotal } from '../lib/hooks/whatsapp/useWhatsAppUnreadTotal';
import { useMetaUnreadTotal } from '../lib/hooks/useMetaUnreadTotal';
import { GmailDrawer } from './mail/GmailDrawer';
import { OutlookDrawer } from './mail/OutlookDrawer';
import { useWhatsAppDrawerStore } from '../lib/stores/whatsappDrawerStore';
import { AiHelpWidget } from './ai-help/AiHelpWidget';
import { useMeetingRecordingStore } from '../lib/stores/meetingRecordingStore';
import { teamChatRealtimeService } from '../lib/services/teamChatRealtimeService';
import { presenceService } from '../lib/services/presenceService';
import { useTeamChatStore } from '../lib/stores/teamChatStore';
import { WelcomeModal, hasSeenWelcomeModal } from './ui/WelcomeModal';
import { shouldShowOnboardingWizard, useOnboarding } from '../lib/hooks/useOnboarding';
import { trackOnboardingEvent } from '../lib/analytics/onboardingEvents';
// SetupChecklist: rimosso. Setup guidato vive solo in floating bottom-right (SetupFloatingBubble in OnboardingRuntime).
// Tour hooks (useDriverTour + useContextualTourTrigger) montati in OnboardingRuntime (App.tsx)
import { DynamicHeaderProvider, useDynamicHeader } from '../contexts/DynamicHeaderContext';
import { DynamicHeader } from './layout/DynamicHeader';
import { useTheme } from '../hooks/useTheme';
import { useAuth, useUserData } from '../lib/auth';
import { usePartnerModel } from '../lib/hooks/usePartnerModel';
import { useSettingsStore } from '../lib/stores/settingsStore';
import { useCalendarStore } from '../lib/stores/calendarStore';
import { supabase } from '../lib/supabase';
import { usePermissionsStore } from '../lib/stores/permissionsStore';
import { useNotificationStore } from '../lib/stores/notificationStore';
import { useApiConfigurationStore } from '../lib/stores/apiConfigurationStore';
import { notificationRealtimeService } from '../lib/services/notificationRealtimeService';
import { logger } from '../lib/utils/logger';
import {
  usePermissions,
  useVisibilityChange,
  useAbortableEffect,
  useDebouncedCallback,
  useBreakpoints
} from '../lib/hooks';
import { useSubscription } from '../lib/hooks/useSubscription';
import type { MenuItem } from '../lib/types';
import { IconRenderer } from '../lib/utils/iconMapper';
import { MENU_CONFIG } from '../lib/config/menuConfig';
import { canAccessBetaFeature, BETA_VOICE_AGENTS_ORG_IDS } from '../lib/config/betaOrgs';
import { injectLockedFeatures } from '../lib/utils/injectLockedFeatures';
import { setBodyScroll } from '../lib/utils/responsive';
import { useCalendarModeSafe } from '../contexts/CalendarModeContext';
import { CalendarSourcesSidebar } from './calendar/CalendarSourcesSidebar';
import { useTeamPlanningModeSafe } from '../contexts/TeamPlanningModeContext';
import { TeamPlanningSidebarLeft } from './planning/TeamPlanningSidebarLeft';
import { useWarehouseModeSafe } from '../contexts/WarehouseModeContext';
import { SettingsSidebarSlotContext } from '../contexts/SettingsSidebarSlotContext';
import { WarehouseSidebar } from './magazzino/WarehouseSidebar';
import { BETA_MODULES } from '../lib/config/featureBundles';
import { MigrationBanner } from './email/MigrationBanner';
import { AddonFeatureLockModal } from './subscription/AddonFeatureLockModal';
import type { AddonId } from '../lib/config/addonLockContent';
import { ADDON_LOCK_CONTENT } from '../lib/config/addonLockContent';

// Mappatura menu item ID → module ID per i badge dei piani
const MENU_TO_MODULE: Record<string, string> = {
  'leads': 'clients',
  'clients': 'clients',
  'contacts': 'contacts',
  'partners': 'clients',
  'deals': 'deals',
  'commissions': 'commissions',
  'services': 'services',
  'inventory': 'inventory',
  'calendar': 'calendar',
  'projects': 'projects',
  'tickets': 'tickets',
  'fattureincloud': 'fattureincloud',
  'service-costs': 'expenses',
  'whatsapp': 'whatsapp',
  'newsletter': 'newsletter',
  'lead-forms': 'lead_forms',
  'qr-codes': 'qr_codes',
  'bookings': 'booking',
  'my-tasks': 'tasks',
  'wiki': 'wiki',
  'surveys': 'surveys',
  'meetings': 'booking',
  'ads': 'marketing',
  'marketing': 'marketing',
  'marketing-dashboard': 'marketing',
  'marketing-campaigns': 'marketing',
  'marketing-attribution': 'marketing',
  // marketing-automation/automation mappati al modulo padre 'marketing'.
  // Originale era 'settings_automation' che NON e presente in enabled_modules
  // di nessun piano standard (Matrix, Plus, ecc.) → bloccava accesso anche con
  // permission settings_automation:view. Il modulo marketing copre tutta la
  // sezione marketing inclusa l'automazione → coerente con UX.
  'marketing-automation': 'marketing',
  'automation': 'marketing',
  'referrers': 'partnerships',
  'partner-tasks': 'partnerships',
  'expenses': 'expenses',
  'rentals': 'rentals',
};

// Mappa completa delle icone con tutte le icone utilizzate (LEGACY - manteniamo per compatibilità)
const iconMap: Record<string, React.ComponentType<any>> = {
  LayoutDashboard,
  LayoutGrid,
  Users,
  FileText,
  Briefcase,
  BarChart,
  Calendar,
  Settings: SettingsIcon,
  DollarSign,
  FolderOpen,
  LifeBuoy,
  File,
  Package,
  ListTodo,
  MessageSquare,
  Building2,
  ClipboardList,
  CalendarClock,
  AlertCircle,
  CreditCard,
  Receipt,
  Handshake,
  UserCheck,
  Send,
  ScrollText,
  ShieldCheck
};

// Menu statici per admin — costanti a livello di modulo per evitare nuove reference ad ogni render
const SUPER_ADMIN_MENU: MenuItem[] = [
  { id: 'organizations', label: 'Organizations', icon: 'Building2', to: '/organizations', order: 0 },
  { id: 'subscription-plans', label: 'Piani Abbonamento', icon: 'Crown', to: '/subscription-plans', order: 1 },
  { id: 'feature-toggles', label: 'Feature Toggle', icon: 'ToggleRight', to: '/admin/feature-toggles', order: 1.5 },
  { id: 'oauth-clients', label: 'App collegate (OAuth)', icon: 'ShieldCheck', to: '/admin/oauth-clients', order: 1.6 },
  { id: 'admin-commerciale', label: 'Commerciale', icon: 'Briefcase', to: '/admin/commerciale', order: 2 },
  { id: 'admin-logs', label: 'Logs', icon: 'ScrollText', to: '/admin/logs', order: 3 },
  { id: 'admin-dpo', label: 'DPO / GDPR', icon: 'ShieldCheck', to: '/admin/dpo', order: 3.5 },
  { id: 'admin-fic-flusia', label: 'Fatturazione Flusia', icon: 'Receipt', to: '/admin/fattureincloud-flusia', order: 4 },
  { id: 'admin-ai-models', label: 'AI Models & Costi', icon: 'Bot', to: '/admin/ai-models', order: 5.5 },
  { id: 'campaigns-monitor', label: 'Campaigns Monitor', icon: 'TrendingUp', to: '/admin/campaigns-monitor', order: 6 },
  { id: 'workflows-ops', label: 'Workflows Ops', icon: 'Workflow', to: '/admin/workflows-ops', order: 6.5 },
  { id: 'core-status', label: 'Core Status', icon: 'Activity', to: '/admin/core-status', order: 6.7 },
  { id: 'admin-ecommerce', label: 'E-commerce', icon: 'ShoppingCart', to: '/admin/ecommerce', order: 6.8 },
  { id: 'trial-dashboard', label: 'Trial Dashboard', icon: 'Hourglass', to: '/admin/trial-dashboard', order: 7 },
  { id: 'user-feedback', label: 'Feedback Utenti', icon: 'MessageCircle', to: '/admin/feedback', order: 8 },
];

const PARTNER_ADMIN_MENU: MenuItem[] = [
  // I nomi contano piu' di quanto sembri: '/partner-admin' elenca le organizzazioni
  // arrivate col codice referral, '/partner-admin/organizzazioni' quelle che il
  // rivenditore attiva e paga. Chiamare la prima "Le Mie Organizzazioni" e la seconda
  // "Abbonamenti" manda il rivenditore sulla pagina sbagliata, dove trova un vuoto che
  // sembra un guasto.
  { id: 'partner-organizations', label: 'Segnalazioni', icon: 'handshake-angle', iconLibrary: 'fa', iconFamily: 'classic', iconStyle: 'solid', to: '/partner-admin', order: 0 },
  { id: 'partner-opportunities', label: 'Opportunità', icon: 'handshake', iconLibrary: 'fa', iconFamily: 'classic', iconStyle: 'solid', to: '/partner-admin/opportunita', order: 1 },
  { id: 'partner-reseller-orgs', label: 'Le tue aziende', icon: 'building', iconLibrary: 'fa', iconFamily: 'classic', iconStyle: 'solid', to: '/partner-admin/organizzazioni', order: 2 },
];

// Stessa regola dei menu sopra: liste fisse, una nuova a ogni render fa ripartire il menu.
const PARTNER_REFERRER_MENU = PARTNER_ADMIN_MENU.filter((v) => v.id !== 'partner-reseller-orgs');
const PARTNER_RESELLER_MENU = PARTNER_ADMIN_MENU.map((v) => (v.id === 'partner-organizations' ? { ...v, label: 'Panoramica' } : v));

interface LayoutProps {
  children: React.ReactNode;
}

// Wrapper che nasconde AiHelpWidget (con animazione) quando il drawer meeting è aperto
function AiHelpWidgetWithDrawerAware() {
  const isMeetingDrawerOpen = useMeetingRecordingStore(s => s.isMeetingDrawerOpen);
  return (
    <div className={`transition-opacity duration-300 ${isMeetingDrawerOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
      <AiHelpWidget />
    </div>
  );
}

// Component interno che usa useDynamicHeader per gestire il padding
function LayoutMainContent({
  isInIframe,
  children
}: {
  isInIframe: boolean;
  children: React.ReactNode;
}) {
  const { isFiltersOpen } = useDynamicHeader();

  return (
    <main className={`flex-1 overflow-x-hidden transition-[padding-top] duration-300 ease-in-out ${
      isInIframe
        ? 'pt-0 pb-0' // No padding in iframe
        : isFiltersOpen
          ? 'pt-0 sm:pt-[calc(3rem+2.5rem)] pb-24 sm:pb-10'  // header (h-12 = 3rem) + filters + bottom padding per scroll
          : 'pt-0 sm:pt-12 pb-24 sm:pb-0'  // header height is h-12 = 3rem
    }`}>
      <MigrationBanner />
      <PaymentStatusBanner />
      {children}
    </main>
  );
}

export function Layout({ children }: LayoutProps) {
  const fluPilot=useFluPilot();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, isSuperAdmin, isPartnerAdmin, partnerAdminId, userProfile, loading: authLoading } = useAuth();
  const partnerModel = usePartnerModel(isPartnerAdmin ? partnerAdminId : null);
  const { organizationId } = useUserData();
  const { drawerOpen } = useApp();
  const { isLight, toggleTheme } = useTheme();
  const searchParams = new URLSearchParams(location.search);

  // Product analytics (PostHog): identifica l'utente loggato per segmentare per
  // organizzazione/ruolo, reset al logout così la sessione successiva (es. altro
  // utente sullo stesso browser) non eredita l'identità precedente.
  useEffect(() => {
    if (userProfile?.id) {
      import('../lib/analytics/posthogClient').then(({ identifyPostHogUser }) => {
        identifyPostHogUser({
          id: userProfile.id,
          email: userProfile.email,
          full_name: userProfile.full_name,
          organization_id: userProfile.organization_id,
          is_super_admin: userProfile.is_super_admin,
        });
      });
    } else if (!authLoading) {
      import('../lib/analytics/posthogClient').then(({ resetPostHogUser }) => resetPostHogUser());
    }
  }, [userProfile?.id, authLoading]);

  // Pageview manuale: SPA (React Router), niente full page load da autocatturare
  useEffect(() => {
    import('../lib/analytics/posthogClient').then(({ trackPageview }) => trackPageview(location.pathname));
  }, [location.pathname]);

  // Global file viewer state
  const [globalFileViewerOpen, setGlobalFileViewerOpen] = useState(false);
  const [globalSelectedFile, setGlobalSelectedFile] = useState<DriveFile | null>(null);

  // Pila: la persona aperta da una scheda nel drawer, chiusa, riporta alla scheda (non chiude tutto)
  const entityDrawer = useRouteDrawer('entityDrawer', { stack: true });
  const dealDrawer = useRouteDrawer('dealDrawer');
  const [, globalDetailsEntityId] = (entityDrawer.id || 'leads:').split(':');
  const globalDetailsModalOpen = !!entityDrawer.id;
  const globalDetailsDrawerClosing = entityDrawer.closing;
  const globalDealDrawerOpen = !!dealDrawer.id;
  const globalDealDrawerEntityId = dealDrawer.id;
  const globalDealDrawerClosing = dealDrawer.closing;
  const closeGlobalDrawer = entityDrawer.close;
  const closeGlobalDealDrawer = dealDrawer.close;
  useDrawerEscape(!!entityDrawer.id && !entityDrawer.closing,entityDrawer.close,55);
  useDrawerEscape(!!dealDrawer.id && !dealDrawer.closing,dealDrawer.close,60);
  const {
    settings,
    loading: settingsLoading,
    error: settingsError,
    initialized,
    retryCount,
    fetchSettings,
    reset: resetSettings
  } = useSettingsStore();
  const {
    unreadCount,
    fetchNotifications
  } = useNotificationStore();
  // Badge globale WhatsApp: somma degli unread di tutte le chat dallo store WhatsApp.
  // Single source of truth allineata con la sidebar (DB Supabase via realtime + polling).
  // Voce di menu "Messaggi" (drawer unico dal 02/09/2026): non letti WhatsApp +
  // chat di team + conversazioni Instagram/Messenger in attesa di risposta.
  // Le chat silenziate non contano (come su WhatsApp).
  const whatsappOnlyUnread = useWhatsAppUnreadTotal();
  const teamUnreadForMenu = useTeamChatStore((s) => s.chats.reduce((sum, c) => sum + (c.unread_count || 0), 0));
  const metaPendingForMenu = useMetaUnreadTotal();
  const whatsappUnreadCount = whatsappOnlyUnread + teamUnreadForMenu + metaPendingForMenu;
  const {
    wahaHealthStatus,
    wahaHealthMessage,
    wahaSessionsIssues,
    fetchWahaHealth,
    subscribeToWahaHealth
  } = useApiConfigurationStore();
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(() => {
    // Carica lo stato dei menu espansi dal localStorage
    const savedExpandedMenus = localStorage.getItem('expanded-menus');
    if (savedExpandedMenus) {
      try {
        const parsed = JSON.parse(savedExpandedMenus);
        return new Set(parsed);
      } catch (error) {
        console.error('[Layout] Errore nel parsing dei menu espansi dal localStorage:', error);
        return new Set();
      }
    }
    return new Set();
  });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showChangelogModal, setShowChangelogModal] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState<{ open: boolean; type: FeedbackType }>({ open: false, type: 'bug' });
  const [feedbackScreenshotBlob, setFeedbackScreenshotBlob] = useState<Blob | null>(null);
  const [feedbackInitialDesc, setFeedbackInitialDesc] = useState<string>('');
  // Stack dell'errore automatico, allegato al context della segnalazione
  const [feedbackErrorStack, setFeedbackErrorStack] = useState<string | undefined>(undefined);
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [lockedAddonOpen, setLockedAddonOpen] = useState<AddonId | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [userProfileImage, setUserProfileImage] = useState<string | null>(null);
  const [userProfileData, setUserProfileData] = useState<{ full_name?: string; organization_id?: string } | null>(null);

  // Voice Agents: la voce di menu "Agenti vocali" compare SOLO dopo che l'org ha
  // collegato il provider (BYOK ElevenLabs dal marketplace). Query leggera RLS-protetta,
  // eseguita solo per le org abilitate alla beta voice.
  const [voiceByokReady, setVoiceByokReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!canAccessBetaFeature(BETA_VOICE_AGENTS_ORG_IDS, organizationId)) {
      setVoiceByokReady(false);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from('org_voice_provider_keys')
        .select('provider_key, enabled')
        .eq('provider_key', 'elevenlabs_api_key')
        .maybeSingle();
      if (!cancelled) setVoiceByokReady(!!data && data.enabled !== false);
    })();
    return () => { cancelled = true; };
  }, [organizationId]);
  const [menuInitialized, setMenuInitialized] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const whatsappDrawerOpen = useWhatsAppDrawerStore(s => s.isOpen);

  // Stato per rilevare se siamo in un iframe
  const [isInIframe, setIsInIframe] = useState(false);

  // Stato per SearchOverlay fullscreen
  const [isSearchOverlayOpen, setIsSearchOverlayOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { results: searchResults, loading: searchLoading, search } = useSearchStore();

  // Calendar mode context (for calendar page sidebar replacement)
  const calendarMode = useCalendarModeSafe();

  // Team Planning mode context (for team planning page sidebar replacement)
  const teamPlanningMode = useTeamPlanningModeSafe();

  // Warehouse mode context (for warehouse page sidebar replacement)
  const warehouseMode = useWarehouseModeSafe();

  // Impostazioni dentro il CRM: al posto del menu del CRM la pagina mette il suo (vedi SettingsLayout).
  // Col menu mobile aperto resta quello del CRM, per spostarsi altrove.
  const [settingsSlotEl, setSettingsSlotEl] = useState<HTMLElement | null>(null);
  const isSettingsSidebar = location.pathname === '/settings' && !mobileMenuOpen;

  // Get Google error from calendar store to pass to context
  const googleError = useCalendarStore(state => state.googleError);

  // Listener per aprire bug report da toast errore
  useEffect(() => {
    const handler = async (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const errorMsg = detail?.errorMessage || '';
      setFeedbackInitialDesc(errorMsg ? `Errore automatico: ${errorMsg}` : '');
      setFeedbackErrorStack(typeof detail?.errorStack === 'string' ? detail.errorStack : undefined);

      // Cattura screenshot prima di aprire il modale
      setCapturingScreenshot(true);
      const blob = await captureScreenshot();
      setCapturingScreenshot(false);
      setFeedbackScreenshotBlob(blob);
      setFeedbackModal({ open: true, type: 'bug' });
    };
    window.addEventListener('open-bug-report', handler);
    return () => window.removeEventListener('open-bug-report', handler);
  }, []);

  // Pass setShowProfileModal to calendar context so CalendarSourcesSidebar can open profile modal
  useEffect(() => {
    if (calendarMode?.setOnOpenProfileModal) {
      calendarMode.setOnOpenProfileModal(() => setShowProfileModal(true));
    }
    return () => {
      if (calendarMode?.setOnOpenProfileModal) {
        calendarMode.setOnOpenProfileModal(null);
      }
    };
  }, [calendarMode?.setOnOpenProfileModal]);

  // Sync googleError from calendarStore to context
  useEffect(() => {
    if (calendarMode?.setGoogleError) {
      calendarMode.setGoogleError(googleError);
    }
  }, [googleError, calendarMode?.setGoogleError]);

  // Subscription status for CTA
  const subscription = useSubscription();

  // Onboarding checklist state (per toggle bottom sidebar quando dismissed)
  const { checklist: onboardingChecklist, restoreChecklist, refetch: refetchOnboarding } = useOnboarding();

  // Quando la bubble chiude la checklist (evento post-persistenza), refetch per
  // far comparire subito l'iconcina in sidebar: il realtime su user_profiles
  // non e' abbastanza affidabile per il live update tra istanze del hook.
  useEffect(() => {
    const onDismissed = () => { void refetchOnboarding(); };
    window.addEventListener('onboarding:checklist-dismissed', onDismissed);
    return () => window.removeEventListener('onboarding:checklist-dismissed', onDismissed);
  }, [refetchOnboarding]);


  // Tour hooks: spostati in OnboardingRuntime (App.tsx)

  // Refresh subscription + permissions quando un altro componente segnala un cambio subscription
  // (es. ritorno da Stripe checkout)
  useEffect(() => {
    const onSubscriptionChanged = async () => {
      subscription.invalidate();
      // Invalida cache menu per forzare ricalcolo con nuovi addon
      filteredMenuCache.current.timestamp = 0;
      // Invalida cache AccessGuard per forzare ri-verifica percorsi
      clearAccessibilityCache();
      try {
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (currentUser) {
          const isSA = currentUser.email?.endsWith('@obly.it') || currentUser.email?.endsWith('@flusia.it') || false;
          await usePermissionsStore.getState().fetchPermissions(currentUser.id, isSA);
        }
      } catch (err) {
        console.error('[Layout] Error refreshing permissions:', err);
      }
    };

    window.addEventListener('subscription-changed', onSubscriptionChanged);
    return () => window.removeEventListener('subscription-changed', onSubscriptionChanged);
  }, [subscription.invalidate]);

  // !isBilledByReseller: i rami 'cancelling'/'expired'/'cancelled' non passano
  // da isTrialExpired, quindi la CTA comparirebbe comunque su un'organizzazione
  // archiviata dal rivenditore. L'upsell non ha senso: paga il partner.
  const showSubscriptionCta = !isSuperAdmin && !isPartnerAdmin && !subscription.isBilledByReseller && (
    subscription.isTrialActive ||
    subscription.isTrialExpired ||
    subscription.organization?.subscription_status === 'cancelling' ||
    subscription.organization?.subscription_status === 'expired' ||
    subscription.organization?.subscription_status === 'canceled'
  );
  const isUrgent = (subscription.trialDaysRemaining ?? 99) <= 3;
  const isCancelling = subscription.organization?.subscription_status === 'cancelling';
  const isExpiredOrCancelled = subscription.isTrialExpired ||
    subscription.organization?.subscription_status === 'expired' ||
    subscription.organization?.subscription_status === 'canceled';

  const ctaLabel = isExpiredOrCancelled
    ? 'Attiva Piano'
    : isCancelling
    ? 'Rinnova Piano'
    : isUrgent
    ? 'Attiva ora!'
    : 'Attiva Piano';

  const ctaSubLabel = subscription.isTrialActive
    ? `Trial: ${subscription.trialDaysRemaining} giorn${subscription.trialDaysRemaining === 1 ? 'o' : 'i'}`
    : isExpiredOrCancelled
    ? 'Trial scaduto'
    : isCancelling
    ? 'In cancellazione'
    : '';

  const ctaGradient = isExpiredOrCancelled
    ? 'from-red-500 to-red-600'
    : isCancelling
    ? 'from-amber-500 to-orange-500'
    : isUrgent
    ? 'from-amber-500 to-orange-500'
    : 'from-blue-500 to-indigo-600';

  // Fetch user profile data for mobile dropdown
  useEffect(() => {
    const fetchUserProfileData = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('full_name, organization_id, avatar_url, profile_image_url')
          .eq('id', user.id)
          .single();

        if (!error && data) {
          setUserProfileData(data);
          // avatar_url (admin-set) ha priorità su profile_image_url (user-set)
          setUserProfileImage(data.avatar_url || data.profile_image_url);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };

    fetchUserProfileData();
  }, [user]);

  // Check if welcome modal should be shown
  useEffect(() => {
    if (user && !authLoading && initialized && !hasSeenWelcomeModal()) {
      // Small delay to ensure the app is fully loaded
      const timer = setTimeout(() => {
        if (!hasSeenWelcomeModal()) setShowWelcomeModal(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [user, authLoading, initialized]);

  // Onboarding v3: auto-espande SetupChecklist al primo accesso (no modal blocking).
  // Condizioni: user authed, profile loaded, non super/partner admin, onboarding mai
  // avviato o non completato. Filosofia: accompagnamento contestuale, non wizard
  // a tutto schermo — vedere docs/superpowers/specs/2026-05-28-onboarding-v3-trial-conversion-design.md
  useEffect(() => {
    if (!user || authLoading || !initialized) return;
    if (isSuperAdmin || isPartnerAdmin) return;
    const onboardingState = (userProfile as { onboarding_state?: unknown } | null)?.onboarding_state ?? null;
    if (shouldShowOnboardingWizard(onboardingState as Parameters<typeof shouldShowOnboardingWizard>[0])) {
      // Sopprime il WelcomeModal vecchio (no doppi popup al primo accesso)
      try {
        localStorage.setItem('flusia-welcome-seen-v2', 'true');
      } catch {
        // localStorage puo fallire in modalita privata, ignoriamo
      }
      setShowWelcomeModal(false);
      // Auto-espande la SetupChecklist solo la prima volta (chiave dedicata di
      // first-touch separata da quella che traccia espansione manuale: cosi se
      // l'utente la collassa una volta, non si riapre da sola ad ogni reload).
      try {
        const firstTouchKey = 'flusia-setup-checklist-first-touch';
        if (!localStorage.getItem(firstTouchKey)) {
          localStorage.setItem('flusia-setup-checklist-expanded', 'true');
          localStorage.setItem(firstTouchKey, '1');
          trackOnboardingEvent('flusia.onboarding.flow_started', { user_id: user.id });
          // Defer dispatch per dare tempo a SetupChecklist di montare
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('onboarding:checklist-restored'));
          }, 200);
        }
      } catch {
        // ignore
      }
    }
  }, [user, authLoading, initialized, isSuperAdmin, isPartnerAdmin, userProfile]);

  // Fetch and subscribe to WhatsApp session health status.
  // La fetch iniziale è ritardata: non deve contendere le connessioni del
  // browser alla pagina che sta caricando (la subscription realtime resta immediata).
  useEffect(() => {
    if (user && !authLoading && initialized) {
      const t = setTimeout(() => fetchWahaHealth(), 8000);
      const unsubscribe = subscribeToWahaHealth();
      return () => { clearTimeout(t); unsubscribe(); };
    }
  }, [user, authLoading, initialized]);

  // Click outside to close user menu (desktop)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showUserMenu]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    setBodyScroll(mobileMenuOpen);

    return () => {
      setBodyScroll(false);
    };
  }, [mobileMenuOpen]);

  // Rileva se siamo in un iframe
  useEffect(() => {
    const inIframe = window.self !== window.top;
    setIsInIframe(inIframe);

    if (inIframe) {
      // Aggiungi classe al body per rimuovere margini sidebar-aware-content
      document.body.classList.add('in-iframe-mode');

      if (process.env.NODE_ENV === 'development') {
        console.log('[Layout] Running inside iframe - hiding sidebar and header');
      }
    } else {
      document.body.classList.remove('in-iframe-mode');
    }

    return () => {
      document.body.classList.remove('in-iframe-mode');
    };
  }, []);

  // Global search shortcut (Cmd+K on Mac, Ctrl+K on Windows) and ESC to close
  useEffect(() => {
    const handleKeyboard = (event: KeyboardEvent) => {
      // Cmd+K (Mac) or Ctrl+K (Windows) to open search overlay
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setIsSearchOverlayOpen(true);
      }

      // Cmd+F / Ctrl+F: let browser search open normally, but also show hint
      if ((event.metaKey || event.ctrlKey) && event.key === 'f') {
        const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
        hotToast(
          `Usa ${isMac ? '⌘' : 'Ctrl+'}K per la ricerca globale`,
          { duration: 3000, position: 'bottom-center', icon: '🔍' }
        );
      }

      // ESC to close search overlay (handled also by SearchOverlay itself)
      if (event.key === 'Escape') {
        setIsSearchOverlayOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('keydown', handleKeyboard);
    return () => {
      document.removeEventListener('keydown', handleKeyboard);
    };
  }, []);

  // Local lead pages own /leads/:id. Global overlays use Router query state,
  // never raw history writes that leave useParams/useLocation out of sync.
  useEffect(() => {
    const lead = (event: Event) => entityDrawer.open(`leads:${(event as CustomEvent).detail.entityId}`);
    const client = (event: Event) => entityDrawer.open(`clients:${(event as CustomEvent).detail.entityId}`);
    const deal = (event: Event) => dealDrawer.open((event as CustomEvent).detail.entityId);
    window.addEventListener('open-lead-modal', lead);
    window.addEventListener('open-client-modal', client);
    window.addEventListener('open-deal-drawer', deal);
    return () => {
      window.removeEventListener('open-lead-modal', lead);
      window.removeEventListener('open-client-modal', client);
      window.removeEventListener('open-deal-drawer', deal);
    };
  }, [entityDrawer.open, dealDrawer.open]);

  // Hook per breakpoints
  const { isTablet } = useBreakpoints();

  const [menuCollapsed, setMenuCollapsed] = useState(() => {
    // Carica lo stato del menu dal localStorage
    const savedState = localStorage.getItem('menu-collapsed');
    const isCollapsed = savedState === 'true';

    // Aggiunge/rimuove la classe sul body per CSS globale
    if (isCollapsed) {
      document.body.classList.add('layout--menu-collapsed');
    } else {
      document.body.classList.remove('layout--menu-collapsed');
    }

    return isCollapsed;
  });

  // Collassa automaticamente il menu su tablet
  useEffect(() => {
    if (isTablet && !menuCollapsed) {
      setMenuCollapsed(true);
      document.body.classList.add('layout--menu-collapsed');
    }
  }, [isTablet]);

  // Ascolta richieste esterne di collapse/expand (es. WhatsApp page)
  useEffect(() => {
    const handler = (e: CustomEvent<{ collapsed: boolean }>) => {
      const { collapsed } = e.detail;
      setMenuCollapsed(collapsed);
      localStorage.setItem('menu-collapsed', String(collapsed));
      if (collapsed) {
        document.body.classList.add('layout--menu-collapsed');
      } else {
        document.body.classList.remove('layout--menu-collapsed');
      }
    };
    window.addEventListener('menu-collapse-request', handler as EventListener);
    return () => window.removeEventListener('menu-collapse-request', handler as EventListener);
  }, []);

  // Gestisci classe body per sidebar espansa nel Team Planning
  useEffect(() => {
    if (teamPlanningMode?.isSidebarExpanded) {
      document.body.classList.add('layout--sidebar-expanded');
    } else {
      document.body.classList.remove('layout--sidebar-expanded');
    }
  }, [teamPlanningMode?.isSidebarExpanded]);

  const notificationLoadRef = React.useRef<{count: number, lastReloadTime?: number}>({count: 0});

  // Caricamento ottimizzato delle notifiche con PageVisibility API
  const loadNotificationsCallback = useCallback((timeLabel = 'manuale') => {
    notificationLoadRef.current.count++;
    const currentCount = notificationLoadRef.current.count;
    
    // Log solo in development e limitato
    if (process.env.NODE_ENV === 'development') {
      //console.log(`[NOTIFICATIONS] Caricamento #${currentCount} (${timeLabel})`);
    }
    
    // Usa direttamente useNotificationStore.getState() per evitare dipendenze nel useCallback
    useNotificationStore.getState().fetchNotifications();
  }, []);
  
  // Usa il nostro hook personalizzato per tracciare la visibilità della pagina
  // Con opzione di ritardo per dare priorità al rendering dell'interfaccia
  const isPageVisible = useVisibilityChange(
    // Callback quando la pagina diventa visibile - ora carica solo le notifiche con ritardo
    () => {
      if (user && notificationLoadRef.current.lastReloadTime) {
        const now = Date.now();
        const lastReload = notificationLoadRef.current.lastReloadTime || 0;
        const timeSinceLastReload = now - lastReload;
        
        // Aggiorna solo le notifiche e solo se sono passati più di 60 secondi
        // Aumentiamo l'intervallo a 60 secondi per ridurre le richieste
        if (timeSinceLastReload > 60000) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`[NOTIFICATIONS] Aggiornamento notifiche dopo ritorno in visibilità (${Math.round(timeSinceLastReload/1000)}s)`);
          }
          // Carica solo le notifiche invece di ricaricare tutto
          useNotificationStore.getState().fetchNotifications();
          notificationLoadRef.current.lastReloadTime = now;
        }
      }
    },
    undefined, // onHidden callback
    { 
      delayCallbackWhenVisible: true, 
      delayMs: 1000  // Ritardo di 1 secondo per dare priorità al rendering UI
    }
  );
  
  // Caricamento iniziale e polling controllato
  useEffect(() => {
    if (!user) return;
    
    // Inizializza ref per tracciare i timestamp
    if (!notificationLoadRef.current.lastReloadTime) {
      notificationLoadRef.current.lastReloadTime = 0;
    }
    
    // Carica subito per la prima volta
    loadNotificationsCallback('iniziale');
    notificationLoadRef.current.lastReloadTime = Date.now();
    
    // Imposta un solo caricamento aggiuntivo dopo 5 secondi
    const initialDelayId = setTimeout(() => {
      loadNotificationsCallback('5s');
      notificationLoadRef.current.lastReloadTime = Date.now();
    }, 5000);
    
    // Polling ottimizzato che si adatta alla visibilità della pagina
    const pollingInterval = 120000; // 2 minuti
    let intervalId: number | null = null;
    
    const startPolling = () => {
      // Pulisci eventuali intervalli esistenti
      if (intervalId) clearInterval(intervalId);
      
      // Crea un nuovo intervallo solo se la pagina è visibile
      if (document.visibilityState === 'visible') {
        intervalId = window.setInterval(() => {
          if (document.visibilityState === 'visible') {
            if (process.env.NODE_ENV === 'development') {
              //console.log(`[NOTIFICATIONS] Aggiornamento periodico (${pollingInterval/1000}s)`);
            }
            loadNotificationsCallback('periodico');
            notificationLoadRef.current.lastReloadTime = Date.now();
          }
        }, pollingInterval);
      }
    };
    
    // Avvia il polling
    startPolling();
    
    // Nota: non aggiungiamo un altro event listener per visibilitychange
    // perché stiamo già usando useVisibilityChange hook sopra
    // Controlliamo lo stato all'avvio del polling
    if (document.visibilityState === 'visible') {
      startPolling();
    }
    
    // Pulizia
    return () => {
      clearTimeout(initialDelayId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [user, loadNotificationsCallback]);

  // Inizializzazione servizio realtime notifiche
  useEffect(() => {
    if (!user?.id || !user?.organization_id) return;

    // Inizializza il servizio realtime per le notifiche
    notificationRealtimeService.init(user.id, user.organization_id);

    // Cleanup: disconnetti quando il componente viene smontato o l'utente cambia
    return () => {
      notificationRealtimeService.disconnect();
    };
  }, [user?.id, user?.organization_id]);

  // Voxloud incoming call toast — listen to realtime notifications
  useEffect(() => {
    if (!user?.id || !user?.organization_id) return;

    const channel = supabase
      .channel('voxloud-incoming-call-toast')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload: any) => {
          const notification = payload.new;
          if (!notification) return;
          const eventType = notification.type || notification.event_type;
          if (eventType !== 'voxloud.incoming_call' && eventType !== 'voxloud.missed_call') return;

          const data = notification.data || {};
          const isMissed = eventType === 'voxloud.missed_call';

          hotToast.custom(
            (t) => (
              <div
                className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg border p-4 max-w-sm ${
                  t.visible ? 'animate-enter' : 'animate-leave'
                }`}
                style={{ borderColor: 'rgba(55,53,47,0.16)' }}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isMissed ? 'bg-red-100' : 'bg-green-100'
                  }`}>
                    <span className="text-lg">{isMissed ? '📞' : '📱'}</span>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-900 dark:text-gray-100">
                      {isMissed ? 'Chiamata persa' : 'Chiamata in arrivo'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {data.client_name || data.phone_number || 'Numero sconosciuto'}
                    </p>
                  </div>
                </div>
                {data.client_id && notification.link && (
                  <button
                    onClick={() => {
                      navigate(notification.link);
                      hotToast.dismiss(t.id);
                    }}
                    className="mt-2 w-full text-center text-xs py-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-blue-600 dark:text-blue-400 transition-colors"
                  >
                    Vai al contatto
                  </button>
                )}
              </div>
            ),
            { duration: isMissed ? 10000 : 15000, position: 'top-right' }
          );
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user?.id, user?.organization_id, navigate]);

  // Animazione titolo scheda per messaggi non letti
  const teamChatUnreadForTitle = useTeamChatStore(state =>
    state.chats.reduce((sum, c) => sum + (c.unread_count || 0), 0)
  );
  useEffect(() => {
    if (teamChatUnreadForTitle === 0) return;
    const originalTitle = document.title.replace(/^💬 \d+ • /, '');
    let alternate = false;
    const id = setInterval(() => {
      document.title = alternate ? originalTitle : `💬 ${teamChatUnreadForTitle} • ${originalTitle}`;
      alternate = !alternate;
    }, 1500);
    return () => {
      clearInterval(id);
      document.title = document.title.replace(/^💬 \d+ • /, '');
    };
  }, [teamChatUnreadForTitle]);

  // Inizializzazione presence service (traccia online/away di tutti gli utenti dell'org)
  useEffect(() => {
    if (!user?.id || !user?.organization_id) return;
    presenceService.init(user.id, user.organization_id, user.full_name || user.email || 'Utente');
    return () => {
      presenceService.disconnect();
    };
  }, [user?.id, user?.organization_id]);

  // Inizializzazione servizio realtime team chat
  useEffect(() => {
    if (!user?.id) return;

    // Inizializza il servizio realtime per la chat team
    teamChatRealtimeService.init(user.id);
    // Fetch iniziale delle chat
    useTeamChatStore.getState().fetchChats();

    // Cleanup: disconnetti quando il componente viene smontato o l'utente cambia
    return () => {
      teamChatRealtimeService.disconnect();
    };
  }, [user?.id]);

  // Gestione riconnessione realtime su visibility change
  useEffect(() => {
    if (!user?.id || !user?.organization_id) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Riconnetti se non connesso
        if (!notificationRealtimeService.isConnected()) {
          notificationRealtimeService.reconnect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id, user?.organization_id]);

  // Feedback visivo per nuovi messaggi WhatsApp
  const showNotificationFeedback = useCallback(() => {
    // Trova il pulsante della campana
    const bellBtn = document.querySelector('.bell-button');
    // Aggiungi un'animazione di pulsazione
    if (bellBtn) {
      bellBtn.classList.add('animate-pulse');
      bellBtn.classList.add('text-red-500');
      // Rimuovi l'animazione dopo 3 secondi
      setTimeout(() => {
        bellBtn.classList.remove('animate-pulse');
        bellBtn.classList.remove('text-red-500');
      }, 3000);
    }
    
    // Forza un aggiornamento delle notifiche per assicurarsi che il contatore sia aggiornato
    useNotificationStore.getState().fetchNotifications();
    
    // Ripeti dopo un breve ritardo per catturare eventuali notifiche ritardate
    setTimeout(() => {
      useNotificationStore.getState().fetchNotifications();
    }, 1000);
  }, []);
  
  // Creazione di una funzione debounced per aggiornare le notifiche
  // Ottimizza le chiamate multiple ravvicinate
  const debouncedUpdateNotifications = useDebouncedCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      //console.log('[WHATSAPP] Aggiornamento debounced notifiche');
    }
    loadNotificationsCallback('messaggio WhatsApp');
    notificationLoadRef.current.lastReloadTime = Date.now();
  }, 800, [loadNotificationsCallback]);
  
  // Sottoscrizione alle notifiche WhatsApp con gestione avanzata degli errori
  useAbortableEffect((signal) => {
    if (!user) return;
    
    logger.log('realtime', 'Inizializzazione listener WhatsApp');
    
    // Usiamo un listener di eventi invece di una sottoscrizione diretta
    // In questo modo riduciamo il numero di canali Supabase simultanei
    const handleWhatsAppMessage = (e: CustomEvent) => {
      // Controlla se l'effetto è stato interrotto
      if (signal.aborted) return;
      
      const message = e.detail;
      console.log('[REALTIME] Ricevuto messaggio da evento custom');
      
      // Visual feedback per l'utente
      showNotificationFeedback();
      
      // MIGLIORATO: Verifica se il messaggio è in entrata (non inviato da noi)
      const isInbound = message?.direction === 'inbound' || 
                        message?.from_me === false || 
                        message?.fromMe === false;
                        
      console.log('[REALTIME] Direzione messaggio:', isInbound ? 'IN' : 'OUT');
      
      // Aggiornamento immediato per messaggi in entrata
      loadNotificationsCallback('messaggio immediato');
      
      // Pianifica un aggiornamento debounced sempre
      debouncedUpdateNotifications();
      
      // Per i messaggi in entrata, aggiorniamo subito le notifiche invece di aspettare il debounce
      if (isInbound) {
        console.log('[REALTIME] Messaggio in entrata - Aggiornamento notifiche');
        useNotificationStore.getState().fetchNotifications();
        
        // Emetti un evento per forzare la rilettura del conteggio in TUTTI i componenti
        // che si basano sul conteggio delle notifiche WhatsApp
        console.log('[REALTIME] Emissione evento aggiornamento badge');
        window.dispatchEvent(new CustomEvent('whatsapp-badge-update'));
        
        // Riaggiorna dopo un breve ritardo per catturare eventuali notifiche ritardate
        setTimeout(() => {
          useNotificationStore.getState().fetchNotifications();
          window.dispatchEvent(new CustomEvent('whatsapp-badge-update'));
        }, 1500);
      }
    };
    
    // Aggiungi il listener personalizzato
    // IMPORTANTE: Deve corrispondere all'evento emesso da whatsappRealtimeService
    window.addEventListener('whatsapp-message-received', handleWhatsAppMessage as EventListener);

    // Pulizia
    return () => {
      console.log('[REALTIME] Rimozione listener messaggi WhatsApp');
      window.removeEventListener('whatsapp-message-received', handleWhatsAppMessage as EventListener);
    };
  }, [user, showNotificationFeedback, loadNotificationsCallback]);

  // Crea una funzione debounced a livello di componente (non all'interno di useEffect)
  const debouncedEventHandler = useDebouncedCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      //console.log('[NOTIFICATIONS] Gestione debounced evento di aggiornamento notifiche WhatsApp');
    }
    
    if (loadNotificationsCallback) {
      loadNotificationsCallback('evento personalizzato');
      if (notificationLoadRef.current) {
        notificationLoadRef.current.lastReloadTime = Date.now();
      }
    }
  }, 500, [loadNotificationsCallback]);
  
  // Nota: rimosso il listener 'whatsapp-notification-update'.
  // Il badge WhatsApp ora deriva direttamente da useWhatsAppChatStore.chats[].unreadCount,
  // che è alimentato da realtime postgres_changes + polling DB Supabase.
  
  // Debug log solo in development e solo una volta all'inizializzazione
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && initialized && !authLoading) {
      /*console.log('[Layout] 📊 Status iniziale:', { 
        user: !!user, 
        userId: user?.id,
        isSuperAdmin, 
        settings: !!settings, 
        menuItems: settings?.menu_settings?.items?.length || 0,
        settingsLoading,
        initialized,
        menuInitialized,
        authLoading,
        retryCount,
        isInitialLoad,
        whatsappUnreadCount
      });*/
    }
  }, [initialized, authLoading]);

  // Ref per tracciare i tentativi di recupero delle impostazioni
  const settingsAttemptRef = useRef<boolean>(false);
  
  // Fix per stato incoerente (initialized: true, ma settings: null)
  useEffect(() => {
    if (initialized && !settings && !settingsLoading && user && !isSuperAdmin && !isPartnerAdmin && !settingsAttemptRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Layout] 🚨 Stato incoerente: initialized=true ma settings=null. Forzando ricaricamento.');
      }
      
      // Imposta il flag per evitare tentativi multipli
      settingsAttemptRef.current = true;
      
      // Solo recupero, senza reset
      fetchSettings().catch(err => {
        console.error('[Layout] ❌ Errore nel ricaricamento impostazioni:', err);
      });
    }
  }, [initialized, settings, settingsLoading, user, isSuperAdmin, fetchSettings, retryCount]);

  // Redirect superadmin/partner admin to their respective pages
  useEffect(() => {
    if (!authLoading && location.pathname === '/dashboard') {
      if (isSuperAdmin) {
        navigate('/organizations');
      } else if (isPartnerAdmin) {
        navigate('/partner-admin');
      }
    }
  }, [isSuperAdmin, isPartnerAdmin, authLoading, location.pathname, navigate]);
  
  // Controlla se dobbiamo aprire il modale del profilo
  useEffect(() => {
    // Se l'URL contiene il parametro openModal=google, apri il modale del profilo
    const shouldOpenModal = searchParams.get('openModal') === 'google';
    
    if (shouldOpenModal) {
      console.log('Opening profile modal from URL param');
      setShowProfileModal(true);
      
      // Rimuovi il parametro openModal dall'URL senza ricaricare la pagina
      if (window.history.replaceState) {
        const newUrl = window.location.pathname;
        window.history.replaceState({ path: newUrl }, '', newUrl);
      }
    }
  }, [location.search]);

    // Import usePermissions hook
  const {
    permissions,
    hasResourceAccess,
    hasPermission,
    availablePermissions,
    loading: permissionsLoading,
    canViewClient: perm_canViewClient,
    canViewLead: perm_canViewLead,
    canViewProject: perm_canViewProject,
    canViewDeal: perm_canViewDeal,
    canViewInventory: perm_canViewInventory,
    canFilterLeadsByUser: perm_canFilterLeadsByUser,
    canFilterClientsByUser: perm_canFilterClientsByUser,
    canFilterDealsByUser: perm_canFilterDealsByUser,
  } = usePermissions();

  // Effetto per ricerca debounced
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        const currentPermissions = {
          canViewClient: perm_canViewClient,
          canViewLead: perm_canViewLead,
          canViewProject: perm_canViewProject,
          canViewDeal: perm_canViewDeal,
          canViewInventory: perm_canViewInventory,
          canFilterLeadsByUser: perm_canFilterLeadsByUser,
          canFilterClientsByUser: perm_canFilterClientsByUser,
          canFilterDealsByUser: perm_canFilterDealsByUser,
        };
        search(searchQuery, currentPermissions);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, search, perm_canViewClient, perm_canViewLead, perm_canViewProject, perm_canViewDeal, perm_canViewInventory, perm_canFilterLeadsByUser, perm_canFilterClientsByUser, perm_canFilterDealsByUser]);

  // Handler per cambiare la query di ricerca
  const handleSearchQueryChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  // Handler per aprire la ricerca
  const handleOpenSearch = useCallback(() => {
    setIsSearchOverlayOpen(true);
  }, []);

  // Handler per chiudere la ricerca
  const handleCloseSearch = useCallback(() => {
    setIsSearchOverlayOpen(false);
    setSearchQuery('');
  }, []);

  // Helper function to check if a menu item is accessible based on permissions
  const canAccessMenuItem = React.useCallback((item: MenuItem): boolean => {
    // Beta rollout gating: voci con _betaOrgIds sono visibili solo alle org elencate
    // (anche superadmin sono filtrati per evitare confusione UX nelle org non-beta)
    if (!canAccessBetaFeature(item._betaOrgIds, organizationId)) return false;

    // Voice Agents: la voce compare solo dopo il collegamento del provider (setup BYOK dal marketplace)
    if (item.id === 'voice-agents' && !voiceByokReady) return false;

    // Super admins can access all menu items
    if (isSuperAdmin) return true;

    // Check subscription module access — nasconde voci menu per moduli non nel piano
    // Applica SOLO quando la subscription è attiva e configurata
    const CONTAINER_IDS = new Set(['crm', 'sales', 'services-inventory', 'activities', 'invoicing', 'work', 'analytics', 'communication', 'settings', 'dashboard']);
    if (!CONTAINER_IDS.has(item.id) && subscription?.isSubscriptionActive) {
      const moduleId = MENU_TO_MODULE[item.id];
      if (moduleId && subscription.canAccessModule && !subscription.canAccessModule(moduleId)) {
        return false;
      }
    }

    // PRIORITY 1: Path-based checks - most reliable since routes don't change with customization
    // This ensures menu items work regardless of renaming or customization
    if (item.to) {
      // Handle known paths
      if (item.to.startsWith('/dashboard')) {
        return hasResourceAccess('dashboard');
      } else if (item.to.startsWith('/clients')) {
        return hasResourceAccess('clients');
      } else if (item.to.startsWith('/leads')) {
        return hasResourceAccess('leads');
      } else if (item.to.startsWith('/contacts')) {
        return hasResourceAccess('contacts');
      } else if (item.to.startsWith('/projects')) {
        return hasResourceAccess('projects');
      } else if (item.to.startsWith('/deals')) {
        return hasResourceAccess('deals');
      } else if (item.to.startsWith('/fattureincloud')) {
        return hasResourceAccess('fattureincloud');
      } else if (item.to.startsWith('/settings')) {
        return hasResourceAccess('settings');
      } else if (item.to.startsWith('/tickets')) {
        return hasResourceAccess('tickets');
      } else if (item.to.startsWith('/analytics')) {
        return hasResourceAccess('reports');
      } else if (item.to.startsWith('/reports')) {
        return hasResourceAccess('reports');
      } else if (item.to.startsWith('/documents')) {
        return hasResourceAccess('documents');
      } else if (item.to.startsWith('/calendar')) {
        return hasResourceAccess('calendar');
      } else if (item.to.startsWith('/inventory') || item.to.startsWith('/magazzino')) {
        return hasResourceAccess('inventory');
      } else if (item.to.startsWith('/whatsapp')) {
        return hasResourceAccess('whatsapp');
      } else if (item.to.startsWith('/commissions')) {
        return hasResourceAccess('commissions');
      } else if (item.to === '/services/costs') {
        return hasResourceAccess('client_expenses');
      } else if (item.to.startsWith('/services')) {
        return hasResourceAccess('services');
      } else if (item.to.startsWith('/payment-methods')) {
        return hasResourceAccess('payment_methods');
      } else if (item.to.startsWith('/referrers')) {
        return hasResourceAccess('referrers');
      } else if (item.to.startsWith('/partners')) {
        return hasResourceAccess('external_partners');
      } else if (item.to.startsWith('/partner-tasks')) {
        return hasResourceAccess('partner_tasks');
      } else if (item.to.startsWith('/fattureincloud')) {
        return hasResourceAccess('fattureincloud');
      } else if (item.to.startsWith('/meetings')) {
        return hasResourceAccess('ai_meeting_bot');
      } else if (item.to.startsWith('/newsletter')) {
        return hasResourceAccess('newsletter');
      } else if (item.to.startsWith('/lead-forms')) {
        return hasResourceAccess('lead_forms');
      } else if (item.to.startsWith('/qr-codes')) {
        return hasResourceAccess('qr_codes');
      } else if (item.to.startsWith('/bookings')) {
        return hasResourceAccess('booking');
      } else if (item.to.startsWith('/expenses')) {
        return hasResourceAccess('client_expenses');
      } else if (item.to === '/services/costs') {
        return hasResourceAccess('services');
      } else if (item.to.startsWith('/wiki')) {
        return hasResourceAccess('wiki');
      } else if (item.to.startsWith('/gmail')) {
        return hasResourceAccess('gmail');
      } else if (item.to.startsWith('/surveys')) {
        return hasResourceAccess('surveys');
      } else if (item.to.startsWith('/my-tasks')) {
        return hasResourceAccess('tasks');
      }
    }

    // PRIORITY 2: Check explicit resource and action properties
    if (item.resource && item.action) {
      return hasPermission(item.resource as any, item.action as any);
    }

    // PRIORITY 3: Check if there's just a resource property
    if (item.resource) {
      return hasResourceAccess(item.resource as any);
    }

    // PRIORITY 4: Check by item ID as fallback (less reliable due to potential customization)
    if (item.id === 'dashboard' || item.id === 'dashboardIBE') {
      return hasResourceAccess('dashboard');
    } else if (item.id === 'deals') {
      return hasResourceAccess('deals');
    } else if (item.id === 'clients') {
      return hasResourceAccess('clients');
    } else if (item.id === 'leads') {
      return hasResourceAccess('leads');
    } else if (item.id === 'projects') {
      return hasResourceAccess('projects');
    } else if (item.id === 'settings') {
      return hasResourceAccess('settings');
    } else if (item.id === 'whatsapp') {
      return hasResourceAccess('whatsapp');
    } else if (item.id === 'services') {
      return hasResourceAccess('services');
    } else if (item.id === 'payment-methods') {
      return hasResourceAccess('payment_methods');
    } else if (item.id === 'referrers') {
      return hasResourceAccess('referrers');
    } else if (item.id === 'partners') {
      return hasResourceAccess('external_partners');
    } else if (item.id === 'contacts') {
      return hasResourceAccess('contacts');
    } else if (item.id === 'fattureincloud') {
      return hasResourceAccess('fattureincloud');
    } else if (item.id === 'my-tasks') {
      return hasResourceAccess('tasks');
    }

    // Special cases for menu containers/groups that don't have direct permissions
    // but contain items that might have permissions
    // Removed explicit checks for my-tasks and newsletter to allow access based on path/resource only

    if (item.id === 'crm') {
      // CRM menu - always visible if user has children access (handled by recursive filtering)
      return true;
    }

    if (item.id === 'sales') {
      // Sales menu - always visible if user has children access
      return true;
    }

    if (item.id === 'services-inventory') {
      // Services & Inventory menu - always visible if user has children access
      return true;
    }

    if (item.id === 'activities') {
      // Activities menu - always visible if user has children access
      return true;
    }

    if (item.id === 'invoicing') {
      // Invoicing menu - always visible if user has children access
      return true;
    }

    if (item.id === 'work') {
      // Work menu is visible if user has access to projects
      return hasResourceAccess('projects');
    }

    if (item.id === 'analytics') {
      // Analytics menu - always visible if user has children access
      return true;
    }

    if (item.id === 'communication') {
      // Communication menu - always visible if user has children access
      return true;
    }
    
    // If we get here, the menu doesn't have explicit permissions defined
    // Default behavior: allow access and let route protection handle actual access control
    // This matches the behavior of the old database-driven menu
    return true;
  }, [isSuperAdmin, hasResourceAccess, hasPermission, subscription, organizationId, voiceByokReady]);

  // Recursively filter menu items based on permissions
  const filterMenuItemsByPermissions = React.useCallback((items: MenuItem[], availablePermissions: any[] = []): MenuItem[] => {
    return items
      // Filter out items that the user can't access via role permissions
      .filter(item => {
        // Always hide "organizations" menu item for non-superadmins
        if (item.id === 'organizations' && !isSuperAdmin) {
          return false;
        }
        return canAccessMenuItem(item);
      })
      // Then recursively filter children
      .map(item => {
        if (item.children && item.children.length > 0) {
          const filteredChildren = filterMenuItemsByPermissions(item.children, availablePermissions);
          // If all children are filtered out, hide this parent too
          if (filteredChildren.length === 0) {
            return { ...item, hidden: true };
          }
          // Otherwise return the item with filtered children
          return { ...item, children: filteredChildren };
        }
        return item;
      })
      // Filter out items marked as hidden during the recursive process
      .filter(item => !item.hidden);
  }, [canAccessMenuItem, isSuperAdmin, availablePermissions]);

  // Cache per gli elementi del menu filtrati
  const filteredMenuCache = useRef<{
    items: MenuItem[];
    settingsVersion: string | null;
    menuLabelsHash: string | null;
    addonsCount: number;
    planId: string | null;
    orgId: string | null;
    voiceByokReady: boolean;
    subscriptionReady: boolean;
    timestamp: number;
  }>({
    items: [],
    settingsVersion: null,
    menuLabelsHash: null,
    addonsCount: 0,
    planId: null,
    orgId: null,
    voiceByokReady: false,
    subscriptionReady: false,
    timestamp: 0
  });

  // Memorizza le voci del menu attuale, filtrate in base ai permessi
  const currentMenuItems = React.useMemo(() => {
    // Se stiamo ancora caricando l'autenticazione, non mostrare menu
    if (authLoading) {
      return [];
    }

    // SuperAdmin special menu
    if (isSuperAdmin) {
      return SUPER_ADMIN_MENU;
    }

    // Partner Admin special menu
    if (isPartnerAdmin) {
      // "Le mie organizzazioni" e' la pagina del rivenditore: al segnalatore
      // mostra solo errori di configurazione e un pulsante che non funziona.
      // Finche' il modello non e' noto teniamo il menu completo com'era prima,
      // cosi' un caricamento lento non fa sparire una voce al rivenditore.
      if (partnerModel === 'segnalatore') {
        return PARTNER_REFERRER_MENU;
      }
      return PARTNER_RESELLER_MENU;
    }

    // CRITICAL FIX: Se permissioni in caricamento O se permissions vuote (race condition)
    // usa la cache se disponibile per evitare menu vuoto durante batching React
    const isLoadingOrEmpty = permissionsLoading || (!availablePermissions || availablePermissions.length === 0);
    if (isLoadingOrEmpty && filteredMenuCache.current.items.length > 0) {
      console.log('[Layout] Usando menu cache durante loading/race condition');
      return filteredMenuCache.current.items;
    }

    // Verifica cache: se la cache non è troppo vecchia (30 minuti), usa quella
    const now = Date.now();
    const cacheAge = now - filteredMenuCache.current.timestamp;
    const isCacheFresh = cacheAge < 30 * 60 * 1000; // 30 minuti

    // Crea hash delle menu_labels per invalidare cache quando cambiano
    const currentMenuLabels = settings?.menu_settings?.menu_labels;
    const currentLabelsHash = currentMenuLabels
      ? `${currentMenuLabels.clients_label || ''}-${currentMenuLabels.leads_label || ''}`
      : '';
    const labelsChanged = filteredMenuCache.current.menuLabelsHash !== currentLabelsHash;
    const currentAddonsCount = subscription?.activeAddons?.length ?? 0;
    const addonsChanged = filteredMenuCache.current.addonsCount !== currentAddonsCount;
    // Invalida anche se il piano cambia (es. caricamento async iniziale o upgrade)
    const currentPlanId = subscription?.plan?.id ?? null;
    const planChanged = filteredMenuCache.current.planId !== currentPlanId;
    // Invalida quando organizationId arriva post-first-render: serve per voci con _betaOrgIds
    // (altrimenti la cache iniziale con orgId=null nasconde la voce per sempre)
    const orgChanged = filteredMenuCache.current.orgId !== organizationId;
    // Invalida quando il BYOK voice viene collegato/scollegato: la voce "Agenti vocali"
    // compare solo dopo il setup del provider.
    const voiceReadyChanged = filteredMenuCache.current.voiceByokReady !== voiceByokReady;
    // Invalida quando la subscription finisce di caricare: il menu calcolato durante
    // il loading è senza hint locked (anti-lampeggio) e non va riservito dalla cache.
    const subscriptionReady = !(subscription?.loading ?? true);
    const subscriptionReadyChanged = filteredMenuCache.current.subscriptionReady !== subscriptionReady;

    if (isCacheFresh && filteredMenuCache.current.items.length > 0 && !labelsChanged && !addonsChanged && !planChanged && !orgChanged && !voiceReadyChanged && !subscriptionReadyChanged) {
      // Cache hit
      if (process.env.NODE_ENV === 'development') {
        //console.log('[Layout] Usando menu items dalla cache', Math.round(cacheAge/1000), 'secondi fa');
      }
      return filteredMenuCache.current.items;
    }

    // Cache miss, ricalcola
    // Use hardcoded menu config — Gmail è accessibile solo dal drawer in header, non dal menu
    const allMenuItems = MENU_CONFIG.map(item => {
      if (item.children) {
        return {
          ...item,
          // I segnalatori sono partner con le provvigioni abilitate: si gestiscono dalla scheda Partner
          children: item.children.filter(child => child.id !== 'gmail'),
        };
      }
      return item;
    });

    // Filter menu items by permissions
    const filteredItems = filterMenuItemsByPermissions(allMenuItems, availablePermissions || [])
      // WhatsApp è stato spostato nell'header — rimuovi dal menu laterale
      .map(item => {
        if (item.children) {
          return { ...item, children: item.children.filter(child => child.id !== 'whatsapp') };
        }
        return item;
      })
      .filter(item => item.id !== 'whatsapp');

    // Apply custom menu labels from settings (with fallback to defaults)
    const applyCustomLabels = (items: MenuItem[]): MenuItem[] => {
      const clientsLabel = settings?.menu_settings?.menu_labels?.clients_label || 'Clienti';
      const leadsLabel = settings?.menu_settings?.menu_labels?.leads_label || 'Leads';

      return items.map(item => {
        // Apply custom label for clients
        if (item.id === 'clients' && clientsLabel !== 'Clienti') {
          item = { ...item, label: clientsLabel };
        }

        // Apply custom label for leads
        if (item.id === 'leads' && leadsLabel !== 'Leads') {
          item = { ...item, label: leadsLabel };
        }

        // Recursively apply to children
        if (item.children && item.children.length > 0) {
          item = { ...item, children: applyCustomLabels(item.children) };
        }

        return item;
      });
    };

    // Inietta voci "locked" — feature non possedute (addon / piano-locked) come hint
    // di discovery. Skippa quelle che l'org HA già attive (role-locked → resta nascosta)
    // e quelle che il ruolo non potrebbe comunque vedere (niente upsell inutile).
    // Disabilitabile via env VITE_FEATURE_LOCKED_DISCOVERY=false.
    const orgHasFeature = (moduleKey: string): boolean => {
      try {
        return subscription?.canAccessModule?.(moduleKey) === true;
      } catch (_) {
        return false;
      }
    };
    // featureKey della registry → resource permessi (stesse resource dei path-check sopra)
    const LOCKED_FEATURE_RESOURCE: Record<string, string> = {
      newsletter: 'newsletter',
      inventory: 'inventory',
      wiki: 'wiki',
      tickets: 'tickets',
      bookings: 'booking',
      'qr-codes': 'qr_codes',
      'screen-recordings': 'screen_recordings',
      surveys: 'surveys',
    };
    const roleCanViewFeature = (featureKey: string): boolean => {
      const resource = LOCKED_FEATURE_RESOURCE[featureKey];
      if (!resource) return true;
      try {
        return hasResourceAccess(resource as any);
      } catch (_) {
        return false;
      }
    };
    // Finché la subscription sta caricando, orgHasFeature risponderebbe false per
    // tutto e ogni voce verrebbe iniettata come comprabile, per poi sparire al
    // caricamento (lampeggio). Meglio nessun hint finché i dati non sono pronti.
    const itemsWithLocked = subscription?.loading
      ? filteredItems
      : injectLockedFeatures(filteredItems, allMenuItems, orgHasFeature, roleCanViewFeature);

    // Pota i contenitori rimasti senza figli dopo tutte le trasformazioni:
    // es. "Comunicazione" quando l'unico figlio visibile (WhatsApp, ora in header)
    // viene rimosso e non ci sono hint locked a ripopolarlo — resterebbe una
    // voce vuota e cliccabile senza destinazione.
    const pruneEmptyContainers = (items: MenuItem[]): MenuItem[] =>
      items
        .map((it) => (it.children ? { ...it, children: pruneEmptyContainers(it.children) } : it))
        .filter((it) => !(it.children && it.children.length === 0));

    const itemsWithCustomLabels = applyCustomLabels(pruneEmptyContainers(itemsWithLocked));

    // Aggiorna la cache
    filteredMenuCache.current = {
      items: itemsWithCustomLabels,
      settingsVersion: null, // Non più necessario con menu hardcoded
      menuLabelsHash: currentLabelsHash,
      addonsCount: currentAddonsCount,
      planId: currentPlanId,
      orgId: organizationId,
      voiceByokReady,
      subscriptionReady,
      timestamp: now
    };

    // Menu ricalcolato: pulisci accessibilityCache così AccessGuard ri-verifica
    // i percorsi con i nuovi menuItems (es. dopo caricamento addon)
    clearAccessibilityCache();

    return itemsWithCustomLabels;
  }, [
    isSuperAdmin,
    // Il modello del partner arriva dopo il primo render (query asincrona):
    // senza questa dipendenza il segnalatore continuerebbe a vedere la voce
    // del rivenditore fino al ricalcolo successivo.
    partnerModel,
    authLoading,
    permissionsLoading,
    filterMenuItemsByPermissions,
    settings,
    // Subscription dependencies — ricalcola il menu quando il piano o gli addon cambiano,
    // così le voci 🔒 di discovery riflettono lo stato reale dell'org.
    subscription?.plan?.id ?? null,
    subscription?.activeAddons?.length ?? 0,
    // Anti-lampeggio: durante il loading non si iniettano hint locked; al termine
    // del caricamento il menu va ricalcolato con i dati veri.
    subscription?.loading ?? true,
    // Beta rollout: ricalcola il menu quando organizationId arriva dopo il primo render,
    // altrimenti le voci con _betaOrgIds restano nascoste per sempre (cache stale).
    organizationId,
    // Voice Agents: ricalcola quando il BYOK viene collegato (voce visibile solo dopo setup).
    voiceByokReady,
  ]);

  // Funzione di inizializzazione del menu
  const initializeMenuExpansion = useCallback(() => {
    if (!menuInitialized && currentMenuItems.length > 0) {
      if (process.env.NODE_ENV === 'development') {
        //console.log('[Layout] 🔍 Inizializzazione espansione menu per percorso:', location.pathname);
      }

      // Parte dai menu già espansi (caricati dal localStorage)
      const newExpandedMenus = new Set<string>(expandedMenus);

      // Funzione ricorsiva per trovare i menu da espandere per il percorso corrente
      const findMenusToExpand = (items: MenuItem[], path: string) => {
        for (const item of items) {
          // Se l'elemento ha figli
          if (item.children && item.children.length > 0) {
            // Controlla se qualsiasi figlio corrisponde al percorso
            const matchingChild = item.children.find(child =>
              (child.to && path === child.to) ||
              (child.to && path.startsWith(child.to + '/'))
            );

            if (matchingChild) {
              if (process.env.NODE_ENV === 'development') {
                //console.log('[Layout] 📂 Espansione menu:', item.id);
              }
              newExpandedMenus.add(item.id);
            }

            // Verifica ricorsivamente i sottolivelli
            findMenusToExpand(item.children, path);
          }
        }
      };

      findMenusToExpand(currentMenuItems, location.pathname);

      // Salva lo stato aggiornato in localStorage
      localStorage.setItem('expanded-menus', JSON.stringify(Array.from(newExpandedMenus)));

      setExpandedMenus(newExpandedMenus);
      setMenuInitialized(true);
    }
  }, [currentMenuItems, location.pathname, menuInitialized, expandedMenus]);

  // Ref per evitare caricamenti ripetuti
  const initialLoadAttemptRef = useRef<boolean>(false);
  
  // Caricamento delle impostazioni solo all'avvio iniziale
  useEffect(() => {
    const loadSettings = async () => {
      if (isInitialLoad && user && !authLoading && !initialized && !settingsLoading && !initialLoadAttemptRef.current) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[Layout] 🚀 Caricamento impostazioni per utente:', user.id);
        }
        
        initialLoadAttemptRef.current = true;
        setIsInitialLoad(false); // Imposta a false per evitare ricaricamenti multipli
        
        try {
          await fetchSettings();
        } catch (error) {
          console.error('[Layout] ❌ Errore nel caricamento impostazioni:', error);
        }
      }
    };
    
    loadSettings();
  }, [user, authLoading, initialized, settingsLoading, fetchSettings, isInitialLoad, retryCount]);

  // Reset delle impostazioni al logout
  useEffect(() => {
    if (!user && initialized) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Layout] 🧹 Reset delle impostazioni e cache dopo logout');
      }
      // Clear localStorage to ensure fresh state
      localStorage.removeItem('settings-storage');
      resetSettings();

      // Reset menu cache to ensure fresh labels on next login
      filteredMenuCache.current = {
        items: [],
        settingsVersion: null,
        menuLabelsHash: null,
        timestamp: 0
      };

      // Ensure permissions cache is cleared
      if (window.clearPermissionsCache) {
        window.clearPermissionsCache();
      }
    }
  }, [user, initialized, resetSettings]);

  // Inizializza l'espansione del menu quando le impostazioni sono caricate
  useEffect(() => {
    if (settings && !menuInitialized) {
      if (process.env.NODE_ENV === 'development') {
        //console.log('[Layout] 📋 Impostazioni caricate, inizializzazione menu');
      }
      initializeMenuExpansion();
    }
  }, [settings, menuInitialized, initializeMenuExpansion]);

  // Espande menu aggiuntivi quando si naviga a nuove sezioni (senza reset completo)
  // Questo evita il re-render completo della sidebar ad ogni cambio route
  // IMPORTANTE: NON includere expandedMenus nelle dipendenze per evitare loop infiniti
  useEffect(() => {
    if (!menuInitialized || currentMenuItems.length === 0) return;

    // Trova se serve espandere un nuovo menu per il path corrente
    const findParentToExpand = (items: MenuItem[], path: string, currentExpanded: Set<string>): string | null => {
      for (const item of items) {
        if (item.children && item.children.length > 0) {
          const matchingChild = item.children.find(child =>
            (child.to && path === child.to) ||
            (child.to && path.startsWith(child.to + '/'))
          );
          if (matchingChild && !currentExpanded.has(item.id)) {
            return item.id;
          }
        }
      }
      return null;
    };

    // Usa la forma funzionale per accedere allo stato corrente senza aggiungerlo alle dipendenze
    setExpandedMenus(prev => {
      const parentToExpand = findParentToExpand(currentMenuItems, location.pathname, prev);
      if (parentToExpand && !prev.has(parentToExpand)) {
        const next = new Set(prev);
        next.add(parentToExpand);
        localStorage.setItem('expanded-menus', JSON.stringify(Array.from(next)));
        return next;
      }
      return prev; // Ritorna lo stesso riferimento se non serve cambiare
    });
  }, [location.pathname, menuInitialized, currentMenuItems]);

  // Gestione dei tentativi falliti con approccio meno invasivo
  useEffect(() => {
    if (retryCount > 0 && retryCount % 3 === 0) {
      console.log('[Layout] 🔄 Troppi tentativi falliti, ripristino delle impostazioni...');
      // Pulisci localStorage per eliminare potenziali dati corrotti
      localStorage.removeItem('settings-storage');
      // Reset delle impostazioni senza ricaricare la pagina
      resetSettings();
      // Riprovare a caricare le impostazioni
      setTimeout(() => {
        fetchSettings().catch(err => {
          console.error('[Layout] ❌ Errore nel ricaricamento dopo reset:', err);
        });
      }, 1000);
    }
  }, [retryCount, resetSettings, fetchSettings]);

  // Applica lo stile di sfondo dalle impostazioni
  // Note: Default background is now managed by ThemeContext in main.tsx
  // This only applies custom backgrounds if set by user
  useEffect(() => {
    if (settings?.background_css) {
      document.body.style.background = settings.background_css;
    }
    // Default backgrounds are now handled by ThemeContext
  }, [settings?.background_css]);

  const toggleMenu = (id: string) => {
    setExpandedMenus(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      // Salva lo stato aggiornato in localStorage
      localStorage.setItem('expanded-menus', JSON.stringify(Array.from(next)));

      return next;
    });
  };

  const handleSignOut = async () => {
    try {
      // Prima del logout, pulisci tutte le cache WhatsApp
      console.log('[Layout] 🧹 Pulizia completa delle cache durante logout');

      // Background reset is now handled by ThemeContext
      console.log('[Layout] 🎨 Background managed by ThemeContext');
      
      // Pulisci tutte le chiavi di cache WhatsApp in sessionStorage
      Object.keys(sessionStorage).forEach(key => {
        if (key.includes('whatsapp_config_cache') || key.includes('waha_config_cache')) {
          console.log(`[Layout] 🗑️ Eliminazione cache WhatsApp: ${key.substring(0, 20)}...`);
          sessionStorage.removeItem(key);
        }
      });
      
      // Pulisci cache sessione completa
      sessionStorage.clear();
      console.log('[Layout] 🗑️ Pulizia completa sessionStorage');
      
      // Pulisci anche localStorage per sicurezza (dati utente, preferenze, ecc.)
      Object.keys(localStorage).forEach(key => {
        if (key.includes('whatsapp') || key.includes('chat') || key.includes('message') || key.includes('background')) {
          console.log(`[Layout] 🗑️ Eliminazione cache localStorage: ${key}`);
          localStorage.removeItem(key);
        }
      });

      // Svuota cache notifiche WhatsApp
      if (window.clearWhatsAppNotificationCache) {
        window.clearWhatsAppNotificationCache();
      }
      
      // Forza pulizia completa delle impostazioni visive
      localStorage.removeItem('settings-storage');
      
      // Ora procedi con il logout normale
      await signOut();
      setShowUserMenu(false);
      resetSettings();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleRefreshSettings = () => {
    if (process.env.NODE_ENV === 'development') {
      //console.log('[Layout] 🔄 Ricaricamento manuale impostazioni...');
    }
    
    resetSettings();
    setTimeout(() => {
      fetchSettings().catch(err => {
        console.error('[Layout] ❌ Errore nel ricaricamento manuale:', err);
      });
    }, 100);
    
    // Forza anche il ricaricamento delle notifiche - utile per debugging
    if (process.env.NODE_ENV === 'development') {
      //console.log('[Layout] 🔔 Forzatura ricaricamento notifiche...');
    }
    
    // Usa il callback creato con useCallback invece di accedere direttamente allo stato
    loadNotificationsCallback('refresh-manuale');
  };


  // Helper function per raccogliere tutti i path definiti nel menu
  const getAllMenuPaths = (): string[] => {
    const paths: string[] = [];

    const collectPaths = (items: MenuItem[]) => {
      items.forEach(item => {
        if (item.to) paths.push(item.to);
        if (item.children) collectPaths(item.children);
      });
    };

    collectPaths(currentMenuItems);
    return paths;
  };

  const isMenuItemActive = (item: MenuItem): boolean => {
    // I Segnalatori stanno dentro Partner (nessuna voce di menu propria)
    if (item.id === 'partners' && location.pathname.startsWith('/referrers')) return true;
    if (item.to) {
      // Verifica corrispondenza esatta
      if (location.pathname === item.to) return true;

      // Verifica se è un percorso annidato (es. /clients/123 deve corrispondere alla voce di menu /clients)
      if (item.to !== '/' && location.pathname.startsWith(item.to + '/')) {
        // Verifica se esiste un altro menu item con un match esatto più specifico
        // Questo previene che /services sia active quando siamo su /services/costs
        const allPaths = getAllMenuPaths();
        const hasMoreSpecificMatch = allPaths.some(
          path => path !== item.to &&
                  path.startsWith(item.to + '/') &&
                  location.pathname === path
        );

        // Se non c'è un match più specifico, attiva questo item
        if (!hasMoreSpecificMatch) return true;
      }
    }

    // Verifica se qualsiasi figlio è attivo
    return item.children?.some(child => isMenuItemActive(child)) || false;
  };

  const renderMenuItem = (item: MenuItem) => {
    if (item.hidden) return null;

    const isWhatsAppItem = item.to === '/whatsapp' || item.id === 'whatsapp';
    const isActive = isWhatsAppItem ? whatsappDrawerOpen : isMenuItemActive(item);
    const isExpanded = expandedMenus.has(item.id);
    const hasChildren = item.children && item.children.length > 0;
    
    // Verifica se è la voce di menu WhatsApp per mostrare il badge
    const isWhatsApp = item.to === '/whatsapp' || item.id === 'whatsapp';
    
    // Accesso sicuro al conteggio, usare direttamente whatsappUnreadCount
    const safeWhatsappCount = typeof whatsappUnreadCount === 'number' ? whatsappUnreadCount : 0;
    const showWhatsAppBadge = isWhatsApp && safeWhatsappCount > 0;
    
    // Determina se mostrare testo delle voci di menu
    // In mobile sempre mostrare il testo, in desktop dipende se il menu è collassato
    const showText = !menuCollapsed || mobileMenuOpen;
    
    // Debug per il badge WhatsApp (solo in development)
    if (isWhatsApp && process.env.NODE_ENV === 'development') {
      /*console.log('[Layout] 📱 Menu WhatsApp badge:', { 
        whatsappUnreadCount, 
        safeCount: safeWhatsappCount,
        shouldShow: showWhatsAppBadge
      });*/
    }

    if (hasChildren) {
      // Section header: in modalita' menu allargato e' un button cliccabile
      // con chevron che apre/chiude i children. In modalita' menu collapsed
      // (sole icone) i children restano sempre visibili come icone.
      const showChildren = !showText || isExpanded;
      return (
        <div key={item.id} className={`${showText ? 'mb-2 mt-4 first:mt-0' : 'mb-1 mt-7 first:mt-0'}`}>
          {showText ? (
            <button
              type="button"
              onClick={() => toggleMenu(item.id)}
              className="menu-section-label w-full flex items-center gap-1 px-3 py-1.5 text-xs font-medium uppercase tracking-wider hover:bg-accent/50 rounded-md transition-colors"
            >
              <ChevronDown
                className={`h-2.5 w-2.5 text-[#E91E8C] transition-transform duration-150 ${isExpanded ? '' : '-rotate-90'}`}
              />
              <span className="flusia-title bg-gradient-to-r from-[#E91E8C] to-[#F97316] bg-clip-text text-transparent">
                {item.label}
              </span>
            </button>
          ) : (
            <div className="px-1 py-1 text-[9px] font-semibold uppercase tracking-wide text-center text-muted-foreground/70">
              {item.label.substring(0, 3)}
            </div>
          )}
          {showChildren && (
            <div className="mt-0.5 space-y-0.5">
              {item.children
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map(child => renderMenuItem(child))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.id}
        to={item.to || '#'}
        data-menu-item={item.id}
        {...(item.id === 'leads' ? { 'data-leads-menu': true } : {})}
        onClick={(e) => {
          // Addon locked: intercetta e mostra AddonFeatureLockModal invece di navigare
          if (item._lockedAddon) {
            e.preventDefault();
            setLockedAddonOpen(item._lockedAddon);
            if (mobileMenuOpen) setMobileMenuOpen(false);
            return;
          }

          // WhatsApp: apri il drawer overlay invece di navigare
          if (isWhatsApp) {
            e.preventDefault();
            useWhatsAppDrawerStore.getState().toggle();
            if (safeWhatsappCount > 0) {
              setTimeout(() => useNotificationStore.getState().fetchNotifications(), 500);
            }
            if (mobileMenuOpen) setMobileMenuOpen(false);
            return;
          }

          // Se siamo già nella sezione, naviga alla root della sezione
          if (isActive && item.to) {
            e.preventDefault();
            navigate(item.to);
          }

          // Su mobile, chiudi il menu dopo il click su un elemento
          if (mobileMenuOpen) {
            setMobileMenuOpen(false);
          }
        }}
        className={`menu-item flex items-center ${showText ? 'px-3' : 'justify-center px-2'} ${mobileMenuOpen ? 'py-3' : 'py-1.5'} text-sm rounded-md transition-colors ${
          isActive ? 'menu-active' : 'hover:bg-accent'
        } ${item._locked ? 'opacity-60 hover:opacity-100' : ''}`}
        style={{ color: 'var(--theme-menu-text)' }}
        {...(!showText
          ? {
              'data-tooltip-id': 'sidebar-tooltip',
              'data-tooltip-content': item._locked
                ? `${item.label} — ${item._lockedBadge || 'Bloccato'}`
                : item.label,
            }
          : {})}
      >
        <div className="relative flex-shrink-0" style={menuCollapsed && !mobileMenuOpen ? { color: '#000' } : undefined}>
          {fluPilot?<FlusiaIcon name={flusiaGlyph(item.to||item.icon)} size={16} className={showText?'mr-2.5':''}/>:<IconRenderer
            iconName={item.icon}
            iconLibrary={item.iconLibrary || 'lucide'}
            iconFamily={item.iconFamily}
            iconStyle={item.iconStyle}
            className={`${showText ? 'mr-2.5' : ''} flex-shrink-0`}
            size={15}
            style={{ width: 15, height: 15 }}
          />}
        </div>
        {showText && (
          <span className={`flex-1 ${mobileMenuOpen ? 'text-base' : 'text-sm'} ${item._locked ? 'text-[#787774]' : ''}`}>
            {item.label}
          </span>
        )}
        {showText && item._locked && (
          <span
            className="ml-2 inline-flex items-center gap-1 px-1.5 py-[1px] text-[10px] font-medium rounded bg-gradient-to-r from-[#E91E8C]/8 to-[#F97316]/8 flex-shrink-0"
            title={`Disponibile: ${item._lockedBadge || ''}`}
          >
            <Lock className="w-2.5 h-2.5 flex-shrink-0" strokeWidth={2.5} style={{ color: '#E91E8C' }} />
            <span
              className="bg-gradient-to-r from-[#E91E8C] to-[#F97316] bg-clip-text text-transparent"
            >
              {item._lockedBadge}
            </span>
          </span>
        )}

        {/* Dot indicator voce attiva */}
        {showText && isActive && (
          <span
            className="ml-2 inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #E91E8C, #F97316)' }}
          />
        )}

        {/* Badge WhatsApp */}
        {showWhatsAppBadge && (
          <span className={`${!showText ? 'absolute -top-1 -right-1' : 'ml-auto'} inline-flex items-center justify-center px-2 py-0.5 min-w-[24px] text-xs font-bold leading-none text-white bg-red-500 rounded-full`}>
            {!showText ? '' : safeWhatsappCount > 99 ? '99+' : safeWhatsappCount}
          </span>
        )}

        {/* Indicatore salute multi-session WhatsApp (espanso 2026-05-30):
            triangolino warning rosso/giallo con count + tooltip per ogni
            numero in problema (sessione rotta o failure rate >15% ultima ora). */}
        {isWhatsApp && wahaSessionsIssues && wahaSessionsIssues.length > 0 && (
          <span
            className={`${!showText ? 'absolute -top-1 -right-1' : 'ml-2'} cursor-help inline-flex items-center gap-0.5`}
            title={
              wahaSessionsIssues.length === 1
                ? `⚠ ${wahaSessionsIssues[0].label}\n${wahaSessionsIssues[0].detail}`
                : `⚠ ${wahaSessionsIssues.length} numeri con problemi:\n` +
                  wahaSessionsIssues.map((i) => `• ${i.label} — ${i.detail}`).join('\n')
            }
          >
            <AlertTriangle
              className={`${showText ? 'h-4 w-4' : 'h-3 w-3'} ${
                wahaHealthStatus === 'error' ? 'text-red-500 fill-red-500/15' : 'text-amber-500 fill-amber-500/15'
              }`}
              strokeWidth={2}
            />
            {showText && wahaSessionsIssues.length > 1 && (
              <span className={`text-[10px] font-bold ${wahaHealthStatus === 'error' ? 'text-red-500' : 'text-amber-500'}`}>
                {wahaSessionsIssues.length}
              </span>
            )}
          </span>
        )}

        {/* Se c'è un badge ma non è WhatsApp */}
        {item.id === 'mytasks' && showText && (
          <span className="ml-auto inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-black bg-black/10 rounded-full">
            3
          </span>
        )}

        {/* Badge Beta */}
        {showText && BETA_MODULES.has(MENU_TO_MODULE[item.id] || item.id) && (
          <span className="ml-auto px-1.5 py-0.5 text-[10px] font-semibold leading-none rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
            Beta
          </span>
        )}
        {!showText && BETA_MODULES.has(MENU_TO_MODULE[item.id] || item.id) && (
          <span className="absolute -top-0.5 -right-0.5 px-1 py-px text-[8px] font-bold leading-none rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
            β
          </span>
        )}

      </Link>
    );
  };

  // Mostra stato di caricamento durante la verifica dell'autenticazione
  if (authLoading) {
    if (process.env.NODE_ENV === 'development') {
      //console.log('[Layout] ⏳ Caricamento autenticazione...');
    }
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-black/40 animate-spin" />
      </div>
    );
  }

  return (
    <SettingsSidebarSlotContext.Provider value={{ element: isSettingsSidebar ? settingsSlotEl : null, collapsed: menuCollapsed }}>
    <DynamicHeaderProvider>
    <div id="layout-root" className="min-h-screen flex" style={{ '--sidebar-width': menuCollapsed ? '64px' : '256px' } as React.CSSProperties}>
      {/* Menu laterale fisso - visibile solo su schermi medi e grandi */}
      {!isInIframe && (
      <div
        className={`glass-menu border border-white/10 rounded-lg flex flex-col ${
          menuCollapsed ? 'w-16' : (teamPlanningMode?.isSidebarExpanded ? 'w-[50vw]' : 'w-64')
        } fixed z-20 transition-all duration-300 transform
          md:translate-x-0 ${mobileMenuOpen ? 'translate-x-0 rounded-none h-screen w-full z-[9999] top-0 left-0 border-none' : '-translate-x-full md:translate-x-0'} md:animate-[fadeInLeft_1.0s_ease-out]`}
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          top: mobileMenuOpen ? '0' : 'calc(env(safe-area-inset-top) + 0.5rem)',
          height: mobileMenuOpen ? '100vh' : 'calc(100vh - env(safe-area-inset-top) - 1rem)'
        }}
      >
        
        {/* Mobile Header with Logo and User Avatar */}
        {mobileMenuOpen && (
          <div className="flex-shrink-0 flex items-center justify-between p-4 border-b md:hidden" style={{ borderColor: 'var(--theme-border)' }}>
            <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
              <img
                src={isLight ? "/logo_colorato.png" : "/logo_bianco.png"}
                alt="Logo Flusia"
                className="h-8 w-auto object-contain"
              />
            </Link>
            <div className="flex items-center space-x-3">
{/* Theme Toggle Button - temporarily hidden
              <button
                onClick={toggleTheme}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-accent transition-colors"
                style={{ color: 'var(--theme-menu-text)' }}
                title={isLight ? 'Passa al tema scuro' : 'Passa al tema chiaro'}
              >
                {isLight ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
              </button>
              */}
              <div className="relative">
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="w-8 h-8 rounded-full bg-[#0f6cbd] flex items-center justify-center text-white text-sm font-medium hover:bg-[#0f6cbd] transition-colors"
                >
                  {userProfileImage ? (
                    <img 
                      src={userProfileImage} 
                      alt="Profile" 
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    user?.email?.charAt(0)?.toUpperCase() || 'U'
                  )}
                </button>
                
                {/* User Dropdown Menu */}
                {showUserDropdown && (
                  <>
                    {/* Backdrop to close dropdown */}
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowUserDropdown(false)}
                    />
                    <div className="absolute right-0 top-10 w-56 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm border rounded-lg shadow-lg z-50 py-1" style={{ borderColor: 'var(--theme-border)' }}>
                      <button
                        onClick={() => {
                          setShowUserDropdown(false);
                          setShowChangelogModal(true);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-accent flex items-center"
                      >
                        <Sparkles className="h-4 w-4 mr-2" />
                        Versione 3.2.0
                      </button>

                      <button
                        onClick={() => {
                          setShowUserDropdown(false);
                          setShowGuideModal(true);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-accent flex items-center"
                      >
                        <HelpCircle className="h-4 w-4 mr-2" />
                        Guida
                      </button>

                      <div className="border-t my-1" style={{ borderColor: 'var(--theme-border)' }} />

                      <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--theme-border)' }}>
                        <p className="text-sm font-medium text-foreground truncate">
                          {userProfileData?.full_name || user?.email || 'Utente'}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {user?.email}
                        </p>
                      </div>

                      <button
                        onClick={() => {
                          setShowUserDropdown(false);
                          setMobileMenuOpen(false);
                          setShowProfileModal(true);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-accent flex items-center"
                      >
                        <UserCircle className="h-4 w-4 mr-2" />
                        Modifica Profilo
                      </button>
                      
                      <div className="border-t border-black/10 my-1" />
                      
                      <button
                        onClick={async () => {
                          setShowUserDropdown(false);
                          setMobileMenuOpen(false);
                          await signOut();
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Disconnetti
                      </button>
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  // Nel calendario il menu e' il pannello dei calendari: si chiude anche quello
                  calendarMode?.setMobileCalendarDrawerOpen?.(false);
                }}
                className="p-1.5 rounded-full hover:bg-accent transition-colors"
                style={{ color: 'var(--theme-menu-text)' }}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
        
        {/* Header - Hidden on mobile when menu is open and when in calendar/team planning mode (those sidebars have their own header) */}
        {!calendarMode?.isCalendarMode && !teamPlanningMode?.isTeamPlanningMode && !warehouseMode?.isWarehouseMode && (
          <div className={`${mobileMenuOpen ? 'hidden' : 'flex'} md:flex h-12 items-center justify-between ${menuCollapsed && !mobileMenuOpen ? 'px-1' : 'px-3'}`}>
            {/* Logo */}
            {!mobileMenuOpen && (
              <>
                {!menuCollapsed ? (
                  <Link to="/dashboard" className="flex items-center hidden md:flex" style={{ paddingLeft: '6px' }} data-logo>
                    <img
                      src={isLight ? "/logo_colorato.png" : "/logo_bianco.png"}
                      alt="Logo"
                      className="h-6 w-auto object-contain"
                    />
                  </Link>
                ) : (
                  /* Il logo resta centrato nei 64px: il chevron "espandi" e'
                     fuori dal flusso (absolute, appoggiato al bordo destro),
                     altrimenti occuperebbe spazio in riga spingendo il logo a
                     sinistra. */
                  <div className="relative hidden md:flex items-center justify-center w-full">
                    <Link to="/dashboard" className="flex items-center justify-center" data-logo>
                      <img
                        src="/icona.png"
                        alt="Logo Icon"
                        className="h-7 w-7 object-contain rounded-lg"
                      />
                    </Link>
                    <button
                      onClick={() => {
                        setMenuCollapsed(false);
                        localStorage.setItem('menu-collapsed', 'false');
                        document.body.classList.remove('layout--menu-collapsed');
                      }}
                      className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center justify-center w-4 h-6 rounded-md bg-white/80 backdrop-blur-[2px] shadow-sm opacity-70 hover:opacity-100 hover:bg-white transition-opacity"
                      style={{ color: '#000' }}
                      title="Espandi menu"
                      aria-label="Espandi menu"
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Pulsante per collassare menu - solo quando espanso */}
            {!menuCollapsed && (
              <button
                onClick={() => {
                  const newState = !menuCollapsed;
                  setMenuCollapsed(newState);
                  localStorage.setItem('menu-collapsed', String(newState));
                  if (newState) {
                    document.body.classList.add('layout--menu-collapsed');
                  } else {
                    document.body.classList.remove('layout--menu-collapsed');
                  }
                }}
                className="hidden md:flex items-center justify-center w-7 h-7 rounded-md hover:bg-black/10 transition-colors"
                style={{ color: 'var(--theme-menu-text)' }}
                title="Collassa menu"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {/* SVG Gradient Definition for Menu Icons */}
        <svg width="0" height="0" style={{ position: 'absolute' }}>
          <defs>
            <linearGradient id="menuIconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0.7" />
            </linearGradient>
          </defs>
        </svg>

        {/* Calendar Mode: Show Calendar Sources Sidebar instead of regular menu */}
        {calendarMode?.isCalendarMode ? (
          <div className="flex-1 flex flex-col min-h-0">
            <CalendarSourcesSidebar
              calendarSources={calendarMode.calendarSources}
              onCalendarSourceToggle={(id) => calendarMode.onCalendarSourceToggle?.(id)}
              onRefreshCalendar={calendarMode.onRefreshCalendar ?? undefined}
              googleLoading={calendarMode.googleLoading}
              googleError={calendarMode.googleError ?? undefined}
              microsoftLoading={calendarMode.microsoftLoading}
              onRefreshMicrosoftCalendar={calendarMode.onRefreshMicrosoftCalendar ?? undefined}
              onOpenProfileModal={calendarMode.onOpenProfileModal ?? undefined}
              canAccessDeals={calendarMode.canAccessDeals}
              canViewBooking={calendarMode.canViewBooking}
              bookingStatusFilters={calendarMode.bookingStatusFilters}
              onBookingStatusFilterToggle={calendarMode.onBookingStatusFilterToggle ?? undefined}
              alfadocsActive={calendarMode.alfadocsActive}
              alfadocsStudios={calendarMode.alfadocsStudios}
              alfadocsStudioFilters={calendarMode.alfadocsStudioFilters}
              onAlfadocsStudioFilterToggle={calendarMode.onAlfadocsStudioFilterToggle ?? undefined}
              translations={calendarMode.translations}
              isCollapsed={menuCollapsed}
              onToggleCollapse={() => {
                const newState = !menuCollapsed;
                setMenuCollapsed(newState);
                localStorage.setItem('menu-collapsed', String(newState));
                if (newState) {
                  document.body.classList.add('layout--menu-collapsed');
                } else {
                  document.body.classList.remove('layout--menu-collapsed');
                }
              }}
              onClose={() => {
                calendarMode.setMobileCalendarDrawerOpen(false);
                setMobileMenuOpen(false);
              }}
              isMobile={mobileMenuOpen}
              currentDate={calendarMode.currentCalendarDate}
              onDateSelect={calendarMode.onDateSelect ? (date: Date) => {
                calendarMode.onDateSelect?.(date);
                // Da telefono, scelto il giorno si torna subito al calendario
                if (mobileMenuOpen) {
                  calendarMode.setMobileCalendarDrawerOpen(false);
                  setMobileMenuOpen(false);
                }
              } : undefined}
              onExitCalendarMode={() => calendarMode.setCalendarMode(false)}
            />
          </div>
        ) : teamPlanningMode?.isTeamPlanningMode ? (
          <div className="flex-1 flex flex-col min-h-0">
            <TeamPlanningSidebarLeft
              isCollapsed={menuCollapsed}
              onToggleCollapse={() => {
                const newState = !menuCollapsed;
                setMenuCollapsed(newState);
                localStorage.setItem('menu-collapsed', String(newState));
                if (newState) {
                  document.body.classList.add('layout--menu-collapsed');
                } else {
                  document.body.classList.remove('layout--menu-collapsed');
                }
              }}
              onClose={() => setMobileMenuOpen(false)}
              isMobile={mobileMenuOpen}
            />
          </div>
        ) : warehouseMode?.isWarehouseMode ? (
          <div className="flex-1 flex flex-col min-h-0">
            <WarehouseSidebar
              activeSection={warehouseMode.activeSection}
              onSectionChange={warehouseMode.setActiveSection}
              isCollapsed={menuCollapsed}
              onToggleCollapse={() => {
                const newState = !menuCollapsed;
                setMenuCollapsed(newState);
                localStorage.setItem('menu-collapsed', String(newState));
                if (newState) {
                  document.body.classList.add('layout--menu-collapsed');
                } else {
                  document.body.classList.remove('layout--menu-collapsed');
                }
              }}
              onClose={() => {
                warehouseMode.setMobileWarehouseDrawerOpen(false);
                setMobileMenuOpen(false);
              }}
              isMobile={mobileMenuOpen}
              onExitWarehouseMode={() => warehouseMode.setWarehouseMode(false)}
            />
          </div>
        ) : isSettingsSidebar ? (
          <div ref={setSettingsSlotEl} className="flex-1 flex flex-col min-h-0" />
        ) : (
          <div className="flex flex-col flex-1 min-h-0">
            <nav className={`${menuCollapsed ? 'px-1 scrollbar-hide' : 'px-2'} space-y-1 overflow-y-auto overflow-x-hidden overscroll-contain ${
              mobileMenuOpen ? 'flex-1 py-2 min-h-0' : 'flex-1 min-h-0'
            } ${mobileMenuOpen ? 'flex flex-col' : ''}`}>
              <div className={`${mobileMenuOpen ? 'flex-1' : ''}`}>
                {/* "Torna al Calendario" button - only visible on calendar page */}
                {location.pathname === '/calendar' && !menuCollapsed && (
                  <button
                    onClick={() => calendarMode?.setCalendarMode(true)}
                    className="w-full flex items-center gap-3 py-2.5 px-3 mt-4 mb-3 rounded-lg border-2 border-[#0f6cbd] hover:bg-[#0f6cbd]/10 transition-colors text-left"
                    style={{ color: 'var(--theme-menu-text)' }}
                  >
                    <Calendar className="h-4 w-4 flex-shrink-0" style={{ color: '#0f6cbd' }} />
                    <span className="text-sm font-medium" style={{ color: '#0f6cbd' }}>Torna al Calendario</span>
                  </button>
                )}
                {/* Collapsed version */}
                {location.pathname === '/calendar' && menuCollapsed && (
                  <button
                    onClick={() => calendarMode?.setCalendarMode(true)}
                    className="w-full flex items-center justify-center py-2 mt-4 mb-2 rounded-lg border-2 border-[#0f6cbd] hover:bg-[#0f6cbd]/10 transition-colors"
                    data-tooltip-id="sidebar-tooltip"
                    data-tooltip-content="Torna al Calendario"
                  >
                    <Calendar className="h-4 w-4" style={{ color: '#0f6cbd' }} />
                  </button>
                )}
                {/* Determina se mostrare lo skeleton del menu
                    Per superadmin/partner admin: non aspetta permissionsLoading perché menu è hardcoded
                    Per utenti normali: aspetta tutti i caricamenti */}
                {(() => {
                  const shouldShowMenuSkeleton = (isSuperAdmin || isPartnerAdmin)
                    ? settingsLoading || !initialized || (retryCount && retryCount < 2)
                    : settingsLoading || permissionsLoading || !initialized || (retryCount && retryCount < 2) || (!settings && !settingsError);

                  return !user ? (
                    <div className="text-center py-4 text-black/60">
                      <p>Non autenticato</p>
                    </div>
                  ) : shouldShowMenuSkeleton ? (
                    <MenuSkeleton />
                  ) : !settings && !isSuperAdmin ? (
                  <div className="flex flex-col items-center justify-center py-4 px-2 text-amber-600">
                    <AlertCircle className="h-6 w-6 mb-2" />
                    <p className="text-center text-sm">
                      Menu non disponibile. <button
                        onClick={handleRefreshSettings}
                        className="underline hover:no-underline"
                      >
                        Riprova
                      </button>
                    </p>
                  </div>
                ) : currentMenuItems && currentMenuItems.length > 0 ? (
                  // Ordina e renderizza le voci di menu
                  [...currentMenuItems]
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((item, index) => (
                      <div key={item.id} className={index === 0 && location.pathname !== '/calendar' ? 'mt-4' : ''}>
                        {renderMenuItem(item)}
                      </div>
                    ))
                ) : (
                  <div className="text-center py-4 text-black/40">
                    {!permissionsLoading && "Nessuna voce di menu disponibile con i tuoi permessi"}
                  </div>
                  );
                })()}
              </div>

              {/* Note: Logout button moved to user dropdown in mobile header */}

              {/* CTA Abbonamento — mobile */}
              {mobileMenuOpen && showSubscriptionCta && (
                <div className="px-3 pb-3 pt-2">
                  <Link
                    to="/settings?tab=subscription"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex flex-col gap-0.5 px-4 py-3 rounded-lg bg-gradient-to-r ${ctaGradient} text-white shadow-sm`}
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold">
                      <Sparkles className="h-4 w-4 flex-shrink-0" />
                      {ctaLabel}
                    </span>
                    {ctaSubLabel && (
                      <span className="text-[11px] text-white/80 pl-6">{ctaSubLabel}</span>
                    )}
                  </Link>
                </div>
              )}
            </nav>

            {/* Tooltip per icone sidebar collassata */}
            {menuCollapsed && !mobileMenuOpen && (
              <ReactTooltip
                id="sidebar-tooltip"
                place="right"
                delayShow={150}
                className="!text-xs !px-2.5 !py-1.5 !rounded-md !z-[9999]"
              />
            )}

            {/* CTA Abbonamento + Bottom icons */}
            {!mobileMenuOpen && (
              <div className={`hidden md:flex flex-col flex-shrink-0 ${menuCollapsed ? 'px-1 items-center' : 'px-3'} pb-3 pt-2 gap-2`}>
                {/* Setup guidato: vive in floating bottom-right via SetupFloatingBubble
                    montato da OnboardingRuntime in App.tsx. Niente piu mount in sidebar. */}

                {/* CTA Abbonamento — visibile solo se non abbonato.
                    Durante il trial attivo il SetupChecklist sopra sostituisce la CTA. */}
                {showSubscriptionCta && !subscription.isTrialActive && (
                  menuCollapsed ? (
                    <Link
                      to="/settings?tab=subscription"
                      className={`flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-r ${ctaGradient} text-white shadow-sm hover:shadow-md transition-all ${isUrgent || isExpiredOrCancelled ? 'animate-pulse' : ''}`}
                      data-tooltip-id="sidebar-tooltip"
                      data-tooltip-content={`${ctaLabel} — ${ctaSubLabel}`}
                    >
                      <Sparkles className="h-4 w-4" />
                    </Link>
                  ) : (
                    <Link
                      to="/settings?tab=subscription"
                      className={`flex flex-col gap-0.5 px-3 py-2.5 rounded-lg bg-gradient-to-r ${ctaGradient} text-white shadow-sm hover:shadow-md hover:scale-[1.02] transition-all`}
                    >
                      <span className="flex items-center gap-2 text-sm font-semibold">
                        <Sparkles className="h-4 w-4 flex-shrink-0" />
                        {ctaLabel}
                      </span>
                      {ctaSubLabel && (
                        <span className="text-[11px] text-white/80 pl-6">{ctaSubLabel}</span>
                      )}
                    </Link>
                  )
                )}

                {/* Bottom icons - Notion style.
                    In menu collassato (64px) tre bottoni da 32px + gap non ci
                    stanno in riga: si impilano in colonna centrata. */}
                <div className={`flex gap-1 ${menuCollapsed ? 'flex-col items-center' : ''}`}>
                  <Link
                    to="/referrals"
                    className={`sidebar-bottom-btn flex items-center justify-center w-8 h-8 rounded-md hover:bg-accent transition-colors ${
                      location.pathname.startsWith('/referrals') ? 'bg-accent' : ''
                    }`}
                    data-tooltip-id="sidebar-tooltip"
                    data-tooltip-content="Referral"
                  >
                    {fluPilot?<FlusiaIcon name="gift" size={16}/>:<svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <defs>
                        <linearGradient id="flusia-gift-gradient" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
                          <stop offset="0%" stopColor="#E91E8C" />
                          <stop offset="55%" stopColor="#F97316" />
                          <stop offset="100%" stopColor="#A855F7" />
                        </linearGradient>
                      </defs>
                      <rect x="3" y="8" width="18" height="4" rx="1" stroke="url(#flusia-gift-gradient)" />
                      <path d="M12 8v13" stroke="url(#flusia-gift-gradient)" />
                      <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" stroke="url(#flusia-gift-gradient)" />
                      <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5" stroke="url(#flusia-gift-gradient)" />
                    </svg>}
                  </Link>
                  {/* Setup guidato — la checklist chiusa NON mostra più nulla di
                      flottante (tondino rimosso): questa iconcina di fianco alle
                      impostazioni è l'unico punto per riaprirla. */}
                  {!isSuperAdmin && !isPartnerAdmin && onboardingChecklist?.dismissed && (
                    <button
                      type="button"
                      onClick={() => {
                        // Forza expanded=true cosi la card si riapre subito espansa, NON collassata
                        try {
                          window.localStorage.setItem('flusia-setup-checklist-expanded', 'true');
                        } catch {
                          // ignore
                        }
                        // Prima il restore su DB, POI l'evento: la bubble al ricevimento
                        // fa refetch e deve trovare dismissed=false gia' persistito
                        // (il realtime da solo non e' affidabile per il re-render live).
                        void (async () => {
                          await restoreChecklist();
                          window.dispatchEvent(new CustomEvent('onboarding:checklist-restored'));
                        })();
                      }}
                      className="sidebar-bottom-btn flex items-center justify-center w-8 h-8 rounded-md hover:bg-accent transition-colors relative"
                      data-tooltip-id="sidebar-tooltip"
                      data-tooltip-content="Riapri setup guidato"
                      aria-label="Riapri setup guidato"
                    >
                      {fluPilot?<FlusiaIcon name="roles" size={16}/>:<ListChecks className="h-[18px] w-[18px]" style={{ strokeWidth: 1.25 }} />}
                      {/* Pallino gradiente per indicare task in sospeso */}
                      <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500" />
                    </button>
                  )}
                  <Link
                    to="/settings"
                    className="sidebar-bottom-btn flex items-center justify-center w-8 h-8 rounded-md hover:bg-accent transition-colors"
                    data-tooltip-id="sidebar-tooltip"
                    data-tooltip-content="Impostazioni"
                  >
                    {fluPilot?<FlusiaIcon name="settings" size={16}/>:<SettingsIcon className="h-[18px] w-[18px]" style={{ strokeWidth: 1.25 }} />}
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Overlay scuro per menu mobile - rimosso per menu a tutto schermo */}

      {/* Contenuto principale che si espande quando il menu laterale è ridotto.
          `min-w-0` (su flex item con flex-1) è critico per mobile: senza, il content
          interno con min-content largo (es. Trial card, tabelle, grafici) forza
          l'espansione del flex item oltre il viewport causando overflow orizzontale
          dell'intera pagina. Non cambia il layout desktop (dove md:ml-* gestisce
          gap con sidebar). */}
      <div className={`layout-content-area flex-1 min-w-0 flex flex-col transition-all duration-300 ${
        isInIframe
          ? '' // No margins in iframe
          : menuCollapsed
            ? 'menu-collapsed md:ml-[calc(4rem+1rem)]'
            : 'md:ml-[calc(16rem+1rem)]'
      }`}>
        {/* Header unificato - DynamicHeader con contenuti di FixedHeader integrati */}
        {!isInIframe && (
        <DynamicHeader
          menuCollapsed={menuCollapsed}
          setShowProfileModal={setShowProfileModal}
          setShowChangelogModal={setShowChangelogModal}
          setShowGuideModal={setShowGuideModal}
          onFeedback={async (type: FeedbackType) => {
            setFeedbackInitialDesc('');
            setCapturingScreenshot(true);
            const blob = await captureScreenshot();
            setCapturingScreenshot(false);
            setFeedbackScreenshotBlob(blob);
            setFeedbackModal({ open: true, type });
          }}
          onFileMaximize={(file) => {
            setGlobalSelectedFile(file);
            setGlobalFileViewerOpen(true);
          }}
          onSignOut={handleSignOut}
          onOpenSearch={handleOpenSearch}
        />
        )}

        {/* Area contenuto con padding adattivo e senza max-width fissa */}
        <LayoutMainContent isInIframe={isInIframe}>
          {/* Renderizza i figli solo se l'utente ha accesso alla pagina corrente */}
          {!isSuperAdmin && user && initialized && !settingsLoading && !permissionsLoading && currentMenuItems.length > 0 ? (
            <AccessGuard
              path={location.pathname}
              menuItems={currentMenuItems}
              settings={settings}
              excludedPaths={['/', '/digital-google-connect', '/digital-microsoft-connect', '/google-auth-callback', '/signature-generator', '/notifications', '/analytics', '/settings', '/unlock', '/permission-mockup', '/referrals']}
            >
              {children}
            </AccessGuard>
          ) : permissionsLoading || settingsLoading || (user && !isSuperAdmin && currentMenuItems.length === 0) ? (
            <MenuSkeleton />
          ) : (
            children
          )}
        </LayoutMainContent>
      </div>
      
      {/* Modal di modifica profilo */}
      <ProfileEditModal 
        isOpen={showProfileModal} 
        onClose={() => setShowProfileModal(false)} 
      />
      
      {/* Mobile Bottom Menu - Hidden when drawer is open, mobile menu is open, or on deal form pages */}
      {(() => {
        const isDealFormRoute = location.pathname.includes('/deals') &&
          (location.search.includes('create=true') ||
           location.search.includes('edit=true') ||
           (location.pathname !== '/deals' && location.pathname.startsWith('/deals/') && location.search.includes('edit=true')));

        return !isInIframe && !drawerOpen && !mobileMenuOpen && !isDealFormRoute;
      })() && (
        <MobileBottomMenu
          onMenuClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          isMenuOpen={mobileMenuOpen}
          onSearchClick={handleOpenSearch}
        />
      )}

      {/* Changelog Modal */}
      <ChangelogModal 
        isOpen={showChangelogModal} 
        onClose={() => setShowChangelogModal(false)} 
      />

      {/* Guide Modal */}
      <GuideModal
        isOpen={showGuideModal}
        onClose={() => setShowGuideModal(false)}
      />

      {/* Overlay cattura screenshot */}
      {capturingScreenshot && (
        <div data-html2canvas-ignore className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3 bg-white dark:bg-[#2C2C2C] rounded-xl px-8 py-6 shadow-2xl">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Cattura screenshot in corso...</span>
          </div>
        </div>
      )}

      {/* Feedback Modal (Bug Report / Feature Request) */}
      <FeedbackModal
        isOpen={feedbackModal.open}
        type={feedbackModal.type}
        onClose={() => { setFeedbackModal({ open: false, type: 'bug' }); setFeedbackScreenshotBlob(null); setFeedbackInitialDesc(''); setFeedbackErrorStack(undefined); }}
        screenshotBlob={feedbackScreenshotBlob}
        initialDescription={feedbackInitialDesc}
        errorStack={feedbackErrorStack}
      />

      {/* Welcome Modal - Flusia introduction */}
      <WelcomeModal
        isOpen={showWelcomeModal}
        onComplete={() => setShowWelcomeModal(false)}
      />

      {/* Search Overlay - Fullscreen search */}
      <SearchOverlay
        isOpen={isSearchOverlayOpen}
        onClose={handleCloseSearch}
        query={searchQuery}
        onQueryChange={handleSearchQueryChange}
        results={searchResults}
        loading={searchLoading}
      />

      {/* Addon Feature Lock Modal — intercetta click su voci sidebar con _lockedAddon */}
      {lockedAddonOpen && (
        <AddonFeatureLockModal
          addonId={lockedAddonOpen}
          open={true}
          onClose={() => setLockedAddonOpen(null)}
          onActivate={() => {
            setLockedAddonOpen(null);
            navigate(`/settings?tab=subscription&addon=${lockedAddonOpen}`);
          }}
          onDemo={() => {
            const content = ADDON_LOCK_CONTENT[lockedAddonOpen];
            if (content) {
              window.open(
                `https://wa.me/393932787927?text=${encodeURIComponent(content.demoMessage)}`,
                '_blank',
                'noopener,noreferrer'
              );
            }
          }}
        />
      )}

      {/* Trial Expired Modal - Global blocking modal */}
      <TrialExpiredModal />

      {/* Organization Archived Modal - Global blocking modal */}
      <OrganizationArchivedModal />

      {/* Past Due Blocked Modal - Global blocking modal (dopo scadenza grace period) */}
      <PastDueBlockedModal />

      {/* WhatsApp Drawer (persistent overlay) */}
      <WhatsAppDrawer />

      {/* Gmail Drawer (persistent overlay) */}
      <GmailDrawer />

      {/* Outlook Drawer (persistent overlay) */}
      <OutlookDrawer />

      {/* Team Chat Widget */}
      <TeamChatWidget />

      {/* AI Help Chat Widget — nascosto in WhatsApp, Marketing e quando il drawer meeting è aperto */}
      {location.pathname !== '/whatsapp' && !location.pathname.startsWith('/marketing') && (
        <AiHelpWidgetWithDrawerAware />
      )}

      {/* Global File Viewer Modal */}
      <FileViewerModal
        file={globalSelectedFile}
        isOpen={globalFileViewerOpen}
        onClose={() => {
          console.log('[Layout] Closing global file viewer modal');
          setGlobalFileViewerOpen(false);
          setGlobalSelectedFile(null);
        }}
      />

      {/* Global Client/Lead Details Drawer */}
      {(globalDetailsModalOpen && globalDetailsEntityId != null || globalDetailsDrawerClosing) && (
        <>
          <div
            className={`fixed inset-0 z-[55] bg-black/10 md:top-12 max-md:!left-0 ${globalDetailsDrawerClosing ? 'animate-[fadeOut_0.25s_ease-in_forwards]' : 'animate-[fadeIn_0.3s_ease-out]'}`}
            style={{ left: 'var(--sidebar-width, 0px)' }}
            onClick={closeGlobalDrawer}
          />
          <div
            className={`fixed top-0 right-0 bottom-0 z-[55] bg-white dark:bg-gray-900 shadow-2xl overflow-y-auto md:top-12 max-md:!left-0 ${globalDetailsDrawerClosing ? 'animate-[slideOutRight_0.25s_ease-in_forwards]' : 'animate-[slideInRight_0.25s_ease-out]'}`}
            style={{ left: 'var(--sidebar-width, 0px)' }}
          >
            <button
              onClick={(e) => { e.stopPropagation(); closeGlobalDrawer(); }}
              className="absolute top-3 left-2 z-50 p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-black/5 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-white/5 transition-colors"
              title={entityDrawer.hasBack ? 'Torna alla scheda precedente' : 'Chiudi'}
            >
              {entityDrawer.hasBack ? <ArrowLeft className="h-4 w-4" /> : <X className="h-4 w-4" />}
            </button>
            <UnifiedDetailsPage
              leadId={globalDetailsEntityId!}
              isModal={true}
              isGlobalDrawer
              onClose={closeGlobalDrawer}
            />
          </div>
        </>
      )}

      {/* Global Deal Drawer (z-40, sopra il drawer Lead/Cliente).
          Usa lo stack di DynamicHeaderContext: l'header si aggiorna alle
          opzioni del Deal mentre e' aperto, e torna alla config
          precedente quando si chiude. */}
      {(globalDealDrawerOpen && globalDealDrawerEntityId != null || globalDealDrawerClosing) && (
        <>
          <div
            className={`fixed inset-0 z-[60] bg-black/10 md:top-12 max-md:!left-0 ${globalDealDrawerClosing ? 'animate-[fadeOut_0.25s_ease-in_forwards]' : 'animate-[fadeIn_0.3s_ease-out]'}`}
            style={{ left: 'var(--sidebar-width, 0px)' }}
            onClick={closeGlobalDealDrawer}
          />
          <div
            className={`fixed top-0 right-0 bottom-0 z-[60] bg-white dark:bg-gray-900 shadow-2xl overflow-y-auto md:top-12 max-md:!left-0 ${globalDealDrawerClosing ? 'animate-[slideOutRight_0.25s_ease-in_forwards]' : 'animate-[slideInRight_0.25s_ease-out]'}`}
            style={{ left: 'var(--sidebar-width, 0px)' }}
          >
            <button
              onClick={(e) => { e.stopPropagation(); closeGlobalDealDrawer(); }}
              className="absolute top-3 left-2 z-50 p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-black/5 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-white/5 transition-colors"
              title="Chiudi"
            >
              <X className="h-4 w-4" />
            </button>
            <DealDetailsPage
              dealId={globalDealDrawerEntityId!}
              isDrawer
              onClose={closeGlobalDealDrawer}
            />
          </div>
        </>
      )}
    </div>
    </DynamicHeaderProvider>
    </SettingsSidebarSlotContext.Provider>
  );
}

// Componente per verificare se l'utente ha accesso alla pagina corrente tramite il menu
interface AccessGuardProps {
  path: string;
  menuItems: MenuItem[];
  settings: any;
  excludedPaths: string[];
  children: React.ReactNode;
}

// Cache per memorizzare i risultati delle verifiche di accesso
// Questo evita di ricalcolare continuamente l'accessibilità delle pagine
// durante i remounting di componenti o i cambi di dimensione della viewport
const accessibilityCache = {
  results: new Map<string, boolean>(),
  // Aggiungiamo un limite massimo per evitare crescita eccessiva
  maxSize: 50,
  // Tempo di validità della cache (in millisecondi)
  validityDuration: 30 * 60 * 1000, // 30 minuti
  // Timestamp dell'ultimo accesso per ogni percorso
  timestamps: new Map<string, number>(),

  // Funzione per ottenere un risultato dalla cache
  get(path: string): boolean | undefined {
    const result = this.results.get(path);
    const timestamp = this.timestamps.get(path);

    // Se non abbiamo un risultato o un timestamp, non è in cache
    if (result === undefined || timestamp === undefined) {
      return undefined;
    }

    // Verifica validità temporale
    const now = Date.now();
    if (now - timestamp > this.validityDuration) {
      // Cache scaduta, rimuovi e ritorna undefined
      this.results.delete(path);
      this.timestamps.delete(path);
      return undefined;
    }

    // Aggiorna il timestamp per refresh della validità
    this.timestamps.set(path, now);
    return result;
  },

  // Funzione per inserire un risultato in cache
  set(path: string, isAccessible: boolean): void {
    // Limita dimensione della cache se necessario
    if (this.results.size >= this.maxSize) {
      // Rimuovi l'entry più vecchia
      let oldestPath: string | null = null;
      let oldestTime = Date.now();

      for (const [p, time] of this.timestamps.entries()) {
        if (time < oldestTime) {
          oldestTime = time;
          oldestPath = p;
        }
      }

      if (oldestPath) {
        this.results.delete(oldestPath);
        this.timestamps.delete(oldestPath);
      }
    }

    // Inserisci il nuovo risultato
    this.results.set(path, isAccessible);
    this.timestamps.set(path, Date.now());
  }
};

// Export function to clear accessibility cache
// This is called when permissions are updated to ensure AccessGuard checks are up-to-date
export function clearAccessibilityCache() {
  accessibilityCache.results.clear();
  accessibilityCache.timestamps.clear();
  console.log('[AccessGuard] Accessibility cache cleared - forcing fresh permission checks');
}

// Add to window for cross-component access (e.g., from RoleManagement after permission updates)
if (typeof window !== 'undefined') {
  (window as any).clearAccessibilityCache = clearAccessibilityCache;
}

function AccessGuard({ path, menuItems, settings, excludedPaths, children }: AccessGuardProps) {
  // Get permissions to check resource access for special cases
  const { hasResourceAccess, hasPermission } = usePermissions();

  // Memo per la verifica di accesso che considera la cache
  const {isAccessible, isExcluded} = React.useMemo(() => {
    // Verifica se il percorso è escluso dal controllo (es. webhook Google, debug)
    const isExcludedPath = excludedPaths.some(excludedPath =>
      path === excludedPath || path.startsWith(`${excludedPath}/`)
    );

    // Se il percorso è escluso, ritorna subito
    if (isExcludedPath) {
      return {isAccessible: true, isExcluded: true};
    }

    // Controllo speciale per path di FattureInCloud
    // Se il path inizia con /fattureincloud o /invoices, verifica l'accesso alla risorsa fattureincloud
    // invece di cercare il path specifico nel menu
    if (path.startsWith('/fattureincloud') || path.startsWith('/invoices')) {
      const hasFICAccess = hasResourceAccess('fattureincloud');
      if (hasFICAccess) {
        return {isAccessible: true, isExcluded: false};
      }
    }

    // Controllo speciale per path di booking dettaglio (/booking/:id)
    // Il menu usa /bookings ma il dettaglio usa /booking/
    if (path.startsWith('/booking/')) {
      const hasBookingAccess = hasResourceAccess('booking');
      if (hasBookingAccess) {
        return {isAccessible: true, isExcluded: false};
      }
    }

    // Segnalatori: dal 16/10/2026 non hanno una voce di menu propria (si aprono dal
    // selettore in Partner), quindi il controllo "il percorso e' nel menu" li bloccava.
    // Stesso permesso della rotta (ProtectedRoute referrers/view).
    if (path === '/referrers' || path.startsWith('/referrers/')) {
      if (hasPermission('referrers', 'view')) {
        return {isAccessible: true, isExcluded: false};
      }
    }

    // Verifica se abbiamo già un risultato in cache
    const cachedResult = accessibilityCache.get(path);
    if (cachedResult !== undefined) {
      return {isAccessible: cachedResult, isExcluded: false};
    }

    // Funzione ricorsiva per verificare se un percorso è accessibile dal menu
    const checkPathAccessible = (items: MenuItem[], currentPath: string): boolean => {
      for (const item of items) {
        // Verifica corrispondenza diretta
        if (item.to && (currentPath === item.to || currentPath.startsWith(`${item.to}/`))) {
          return true;
        }

        // Verifica ricorsivamente nei figli
        if (item.children && item.children.length > 0) {
          if (checkPathAccessible(item.children, currentPath)) {
            return true;
          }
        }
      }

      return false;
    };

    // Calcola e salva in cache il risultato
    const result = checkPathAccessible(menuItems, path);
    accessibilityCache.set(path, result);

    return {isAccessible: result, isExcluded: false};
  }, [path, menuItems, excludedPaths, hasResourceAccess, hasPermission]); // Dipendenze per il memo

  // Se il percorso è escluso, renderizza i children normalmente
  if (isExcluded) {
    return <>{children}</>;
  }

  // BYPASS PER PAGINE ESSENZIALI (dopo useMemo per rispettare Rules of Hooks)
  const defaultRoute = settings?.default_route || '/dashboard';
  if (path === defaultRoute || path.startsWith('/dashboard') || path === '/login' || path === '/register') {
    return <>{children}</>;
  }

  // Se il percorso non è accessibile, mostra la pagina di errore permessi
  if (!isAccessible) {
    // Rileva se stiamo in una finestra con viewport ridotta (es. dev tools aperti)
    const isSmallViewport = window.innerWidth < 1200;
    if (isSmallViewport) {
      console.warn(`[AccessGuard] Accesso consentito per viewport ridotta: ${path}`);
      return <>{children}</>;
    }

    // Estrai il nome della risorsa dal percorso per una migliore UX
    const pathSegments = path.split('/').filter(Boolean);
    const resource = pathSegments[0] || 'questa sezione';

    console.log(`[AccessGuard] Accesso negato per: ${path}`);
    return <PermissionDeniedPage resource={resource} action="visualizzare" />;
  }

  // Se il percorso è accessibile, renderizza i children
  return <>{children}</>;
}